import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { APP } from './tokens';
import FolderPickerButton from './FolderPickerButton';
import PaginatedDocument from './PaginatedDocument';
import PresenceAvatars from './PresenceAvatars';
import LiveCursors from './LiveCursors';
import CommentPins from './CommentPins';
import PdfSigningViewer from '../pdfViewer/PdfSigningViewer';
import type { PlacementType } from '../pdfViewer/AddFieldToolbar';
import type { SignatureField } from '../../types';
import type { ReviewSuggestion } from '../../types/review';
import ReviewUndoToast from './review/ReviewUndoToast';
import DocumentTextView from './DocumentTextView';
import InlineCommentForm from './InlineCommentForm';
import CoTActivityCard from './cot/CoTActivityCard';
import type { CoTState } from '../../hooks/useCoTState';
import MobileDocumentHeader from './mobile/MobileDocumentHeader';
import MobileVersionSheet from './mobile/MobileVersionSheet';
import RiskReviewEntryBanner from './clauseReview/RiskReviewEntryBanner';
import { docVersionDebugLog } from '../../lib/documentVersionDebug';

import { PAGE_WIDTH, PAGE_HEIGHT } from './pageConstants';

function isEditableCopyFromSignedPdfVersion(version?: import('../../types').DocumentVersionSummary | null): boolean {
  return Boolean(
    version &&
      version.source === 'conversion' &&
      version.lineageKind === 'copy' &&
      version.changeSummary === 'Editable copy from signed PDF',
  );
}

function isPdfDocument(fileName?: string | null, mimeType?: string | null): boolean {
  const mime = (mimeType ?? '').toLowerCase();
  return mime === 'application/pdf' || mime.includes('/pdf') || /\.pdf$/i.test(fileName ?? '');
}

function isDocxDocument(fileName?: string | null, mimeType?: string | null): boolean {
  if (/\.pdf$/i.test(fileName ?? '')) return false;
  const mime = (mimeType ?? '').toLowerCase();
  return (
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mime === 'application/msword' ||
    /\.(docx|doc)$/i.test(fileName ?? '')
  );
}

function EditableDocxRecoverySurface({
  fileName,
  filePreviewUrl,
  activeVersion,
  onDownload,
}: {
  fileName?: string | null;
  filePreviewUrl?: string | null;
  activeVersion?: import('../../types').DocumentVersionSummary | null;
  onDownload?: () => void;
}) {
  const isRecoveryCopy = isEditableCopyFromSignedPdfVersion(activeVersion);
  return (
    <div
      style={{
        width: 'min(100%, 720px)',
        marginTop: 56,
        borderRadius: 8,
        border: `1px solid ${APP.border}`,
        backgroundColor: APP.bgDocSheet,
        boxShadow: APP.docSheetShadow,
        padding: '28px 32px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        fontFamily: APP.fontFamily,
        color: APP.textPrimary,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <VersionTreeIcon kind="file" mimeType="application/vnd.openxmlformats-officedocument.wordprocessingml.document" />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 750, lineHeight: '20px' }}>
            {isRecoveryCopy ? 'Editable copy from signed PDF' : 'Word document'}
          </div>
          <div style={{ marginTop: 3, fontSize: 12, color: APP.textSecondary, lineHeight: '17px', overflowWrap: 'anywhere' }}>
            {fileName ?? activeVersion?.fileName ?? 'Document.docx'}
          </div>
        </div>
      </div>
      <p style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: APP.textCardDetail }}>
        This is an editable DOCX copy. Download it to edit in Word or Pages, then reimport it when you are ready to continue.
      </p>
      {isRecoveryCopy && (
        <div
          style={{
            borderRadius: 6,
            backgroundColor: '#F4F1EC',
            border: `1px solid ${APP.border}`,
            padding: '9px 11px',
            fontSize: 12,
            lineHeight: '17px',
            color: APP.textCardDetail,
          }}
        >
          Corner preserved the signed PDF unchanged and created this editable branch for revisions.
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onDownload}
          style={{
            borderRadius: 6,
            border: 'none',
            backgroundColor: APP.textDark,
            color: '#FDFCFB',
            padding: '9px 14px',
            fontFamily: APP.fontFamily,
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Download DOCX
        </button>
        {filePreviewUrl && (
          <a
            href={filePreviewUrl}
            download={fileName ?? activeVersion?.fileName ?? 'document.docx'}
            style={{
              color: APP.textSecondary,
              fontSize: 12,
              fontWeight: 650,
              textDecoration: 'none',
            }}
          >
            Open file
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Image action chips ─────────────────────────

export interface ImageAction {
  label: string;
  accent?: boolean;
}

const IMAGE_ACTIONS: ImageAction[] = [
  { label: 'Remove background' },
  { label: 'Describe image' },
  { label: 'Extract text' },
  { label: '+ Ask', accent: true },
];

// ─── Image comparison types ─────────────────────

export interface ImageResult {
  /** URL of the processed result image */
  url: string;
  fileName: string;
  fileSize: string;
  format: string;
  /** e.g. "BG removed" */
  transformLabel: string;
}

// ─── Sub-components ─────────────────────────────

function PaginationBar({
  currentPage,
  totalPages,
  zoom,
  onZoomIn,
  onZoomOut,
  onFitToScreen,
  variantLabel,
  onVariantAction,
}: {
  currentPage: number;
  totalPages: number;
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitToScreen: () => void;
  variantLabel?: string;
  onVariantAction?: (action: 'retrieve_original' | 'retrieve_with_prompt') => void;
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const pillRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handle = (e: MouseEvent) => {
      if (pillRef.current && !pillRef.current.contains(e.target as Node)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [dropdownOpen]);

  const PAG_TEXT: React.CSSProperties = { fontSize: 11, color: APP.textSecondary, fontFamily: APP.fontFamily, lineHeight: '14px', flexShrink: 0 };
  const PAG_BTN: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: APP.textSecondary, fontFamily: APP.fontFamily, lineHeight: '16px', padding: 0 };
  const PAG_SEP: React.CSSProperties = { width: 1, height: 14, backgroundColor: APP.border, flexShrink: 0 };

  return (
    <div
      style={{
        height: 36,
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        paddingInline: 12,
        gap: 12,
        backgroundColor: APP.bgPaginationBar,
        borderBottom: `1px solid ${APP.border}`,
      }}
    >
      {/* Page counter + zoom controls (right-aligned) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
        {/* Page counter */}
        <span style={PAG_TEXT}>
          {currentPage} / {totalPages}
        </span>

        {/* Separator */}
        <div style={PAG_SEP} />

        {/* Zoom out */}
        <button type="button" onClick={onZoomOut} style={PAG_BTN}>−</button>

        {/* Zoom level */}
        <span style={PAG_TEXT}>{zoom}%</span>

        {/* Zoom in */}
        <button type="button" onClick={onZoomIn} style={PAG_BTN}>+</button>

        {/* Separator */}
        <div style={PAG_SEP} />

        {/* Fit to screen */}
        <button type="button" onClick={onFitToScreen} style={{ ...PAG_BTN, padding: 3 }} title="Fit to screen">
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke={APP.textSecondary} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1.5" y="1.5" width="10" height="10" rx="1.5" />
          </svg>
        </button>
      </div>

      {/* Right: variant pill with dropdown */}
      {variantLabel && onVariantAction && (
        <div ref={pillRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setDropdownOpen((o) => !o)}
            style={{
              display: 'flex',
              alignItems: 'center',
              height: 28,
              borderRadius: 6,
              paddingLeft: 6,
              paddingRight: 8,
              gap: 6,
              backgroundColor: APP.bgQuickAction,
              border: `1px solid ${APP.border}`,
              cursor: 'pointer',
              fontFamily: APP.fontFamily,
            }}
          >
            {/* Mini document icon */}
            <div style={{
              width: 18, height: 22, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'flex-end', flexShrink: 0, borderRadius: 2, paddingBottom: 3, gap: 2,
              backgroundColor: APP.bgVersionCard, border: `1px solid ${APP.border}`,
            }}>
              <div style={{ width: 10, height: 1.5, borderRadius: 1, backgroundColor: '#C8C0B6' }} />
              <div style={{ width: 10, height: 1.5, borderRadius: 1, backgroundColor: '#C8C0B6' }} />
              <div style={{ width: 7, height: 1.5, borderRadius: 1, backgroundColor: '#C8C0B6' }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 400, color: APP.textSecondary, lineHeight: '16px', flexShrink: 0 }}>
              {variantLabel}
            </span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={APP.textSecondary} strokeWidth="2.5" style={{ flexShrink: 0 }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {/* Dropdown */}
          {dropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: 34,
                right: 0,
                minWidth: 220,
                borderRadius: 8,
                overflow: 'clip',
                backgroundColor: '#FEFCF9',
                border: '1px solid #E0DCD4',
                boxShadow: '0 4px 16px rgba(26, 23, 20, 0.08)',
                zIndex: 20,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <button
                type="button"
                onClick={() => { setDropdownOpen(false); onVariantAction('retrieve_original'); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 12px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: APP.fontFamily,
                  fontSize: 13,
                  fontWeight: 400,
                  color: '#1A1714',
                  lineHeight: '16px',
                  textAlign: 'left',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1A1714" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <path d="M3 15v4c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2v-4M17 9l-5 5-5-5M12 12.8V2.5" />
                </svg>
                Retrieve original doc
              </button>
              <div style={{ height: 1, marginInline: 12, backgroundColor: '#F0EAE3', flexShrink: 0 }} />
              <button
                type="button"
                onClick={() => { setDropdownOpen(false); onVariantAction('retrieve_with_prompt'); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 12px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: APP.fontFamily,
                  fontSize: 13,
                  fontWeight: 400,
                  color: '#1A1714',
                  lineHeight: '16px',
                  textAlign: 'left',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1A1714" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                Retrieve with a new prompt
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// CORNER_V2: DELETED — SignatureFieldCard + SignatureFieldOverlay (260 lines removed).
// These rendered a sidebar card list of signature fields below the iframe.
// Replaced by PdfSigningViewer's built-in FieldOverlay with Corner-branded markers.
// See: components/pdfViewer/FieldMarker.tsx, FieldOverlay.tsx, PdfSigningViewer.tsx

// ─── Image action chip bar ──────────────────────

function ImageActionBar({
  actions = IMAGE_ACTIONS,
  onActionClick,
}: {
  actions?: ImageAction[];
  onActionClick?: (label: string) => void;
}) {
  return (
    <div
      style={{
        height: 44,
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        paddingInline: 16,
        gap: 8,
        backgroundColor: '#FAFAF9',
        borderTop: `1px solid ${APP.border}`,
      }}
    >
      {actions.map((a) => (
        <button
          key={a.label}
          type="button"
          onClick={() => onActionClick?.(a.label)}
          style={{
            borderRadius: 20,
            paddingBlock: 5,
            paddingInline: 12,
            backgroundColor: '#FFFFFF',
            border: `1px solid ${APP.borderQuickAction}`,
            cursor: 'pointer',
            fontFamily: APP.fontFamily,
            fontSize: 12,
            fontWeight: 400,
            color: a.accent ? APP.textImageArrowLabel : '#6B5F59',
            lineHeight: '16px',
          }}
        >
          {a.label}
        </button>
      ))}
    </div>
  );
}

// ─── Image comparison view ──────────────────────

const CHECKERBOARD_BG = `
  linear-gradient(45deg, #D4CCC3 25%, transparent 25%),
  linear-gradient(-45deg, #D4CCC3 25%, transparent 25%),
  linear-gradient(45deg, transparent 75%, #D4CCC3 75%),
  linear-gradient(-45deg, transparent 75%, #D4CCC3 75%)
`.trim();

function ImageCard({
  src,
  label,
  fileName,
  badge,
  transparent,
  dimensions,
}: {
  src?: string;
  label: string;
  fileName: string;
  badge?: string;
  transparent?: boolean;
  dimensions?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      {/* Label */}
      <span
        style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: '0.06em',
          textTransform: 'uppercase' as const,
          color: APP.textImageLabel,
          fontFamily: APP.fontFamily,
          lineHeight: '14px',
        }}
      >
        {label}
      </span>

      {/* Image */}
      <div
        style={{
          width: 240,
          height: 300,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 8,
          overflow: 'clip',
          flexShrink: 0,
          backgroundColor: transparent ? APP.bgResultCheckerboard : APP.bgImageCard,
          ...(transparent ? {
            backgroundImage: CHECKERBOARD_BG,
            backgroundSize: '16px 16px',
            backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
          } : {}),
        }}
      >
        {src && (
          <img
            src={src}
            alt={fileName}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        )}

        {/* Badge (dimensions or format) */}
        {(badge || dimensions) && (
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              right: 10,
              borderRadius: 4,
              paddingBlock: 2,
              paddingInline: 6,
              backgroundColor: APP.bgImageBadge,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 400,
                color: APP.textImageBadge,
                fontFamily: APP.fontFamily,
                lineHeight: '12px',
              }}
            >
              {badge ?? dimensions}
            </span>
          </div>
        )}
      </div>

      {/* Caption */}
      <span
        style={{
          fontSize: 11,
          fontWeight: 400,
          color: APP.textImageCaption,
          fontFamily: APP.fontFamily,
          lineHeight: '14px',
        }}
      >
        {fileName}
      </span>
    </div>
  );
}

function TransformArrow({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: -20, gap: 4 }}>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={APP.textImageArrowLabel} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </svg>
      <span
        style={{
          fontSize: 10,
          fontWeight: 500,
          color: APP.textImageArrowLabel,
          fontFamily: APP.fontFamily,
          lineHeight: '12px',
        }}
      >
        {label}
      </span>
    </div>
  );
}

// ─── Processing Overlay ─────────────────────────

const SHIMMER_KEYFRAMES = `
@keyframes cornerShimmer {
  0% { background-position: -260px 0; }
  100% { background-position: 260px 0; }
}
@keyframes cornerDotPulse {
  0%, 100% { opacity: 0.25; }
  50% { opacity: 1; }
}
`;

/** Non-blocking status while the draft panel streams — keeps the editor visible underneath. */
function DraftStreamingIndicator({ label, progress }: { label: string; progress?: number }) {
  const pct = typeof progress === 'number' && Number.isFinite(progress)
    ? Math.max(0, Math.min(100, Math.round(progress)))
    : null;
  return (
    <div
      style={{
        position: 'absolute',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        maxWidth: 'min(420px, calc(100% - 32px))',
        padding: '7px 14px',
        borderRadius: 999,
        backgroundColor: 'rgba(253, 252, 251, 0.96)',
        border: `1px solid ${APP.border}`,
        boxShadow: '0 6px 24px rgba(45,36,28,0.10)',
        fontFamily: APP.fontFamily,
        fontSize: 12,
        fontWeight: 500,
        color: APP.textPrimary,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      <style>{SHIMMER_KEYFRAMES}</style>
      <div
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          backgroundColor: '#D46A3A',
          flexShrink: 0,
          animation: 'cornerDotPulse 1.4s ease-in-out infinite',
        }}
      />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label || 'Writing your document…'}
      </span>
      {pct != null && (
        <div
          style={{
            position: 'absolute',
            left: 10,
            right: 10,
            bottom: 3,
            height: 2,
            borderRadius: 999,
            backgroundColor: '#EFE7DE',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              borderRadius: 999,
              backgroundColor: '#D46A3A',
              transition: 'width 0.18s ease',
            }}
          />
        </div>
      )}
    </div>
  );
}

function DraftSkeletonDocument() {
  const lineWidths = ['54%', '88%', '82%', '76%', '62%'];
  const sections = ['1. DOCUMENT OVERVIEW', '2. KEY TERMS', '3. RESPONSIBILITIES'];
  return (
    <div
      aria-hidden
      style={{
        width: PAGE_WIDTH,
        maxWidth: '100%',
        minHeight: PAGE_HEIGHT,
        borderRadius: 8,
        border: `1px solid ${APP.border}`,
        backgroundColor: APP.bgCardBody,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        padding: '72px 72px',
        boxSizing: 'border-box',
        fontFamily: APP.fontFamily,
        color: APP.textPrimary,
      }}
    >
      <style>{SHIMMER_KEYFRAMES}</style>
      <div
        style={{
          width: '46%',
          height: 22,
          borderRadius: 4,
          background: 'linear-gradient(90deg, #EDE7DF 25%, #E2DBD2 50%, #EDE7DF 75%)',
          backgroundSize: '520px 22px',
          animation: 'cornerShimmer 1.8s ease-in-out infinite',
          marginBottom: 34,
        }}
      />
      {sections.map((section, i) => (
        <div key={section} style={{ marginBottom: i === sections.length - 1 ? 0 : 30 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0,
              color: APP.textSecondary,
              marginBottom: 12,
            }}
          >
            {section}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {lineWidths.slice(0, i === 0 ? 4 : 5).map((width, j) => (
              <div
                key={`${section}-${j}`}
                style={{
                  width,
                  height: 9,
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #F0EAE3 25%, #E6DED5 50%, #F0EAE3 75%)',
                  backgroundSize: '520px 9px',
                  animation: 'cornerShimmer 1.8s ease-in-out infinite',
                }}
              />
            ))}
          </div>
        </div>
      ))}
      <div
        style={{
          marginTop: 34,
          fontSize: 12,
          color: APP.textMuted,
        }}
      >
        Filling in...
      </div>
    </div>
  );
}

function ProcessingOverlay() {
  const barWidths = [260, 221, 239, 182];
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        backgroundColor: '#FDFCFB8C',
        zIndex: 10,
      }}
    >
      <style>{SHIMMER_KEYFRAMES}</style>
      {/* Skeleton shimmer bars */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: 260 }}>
        {barWidths.map((w, i) => (
          <div
            key={i}
            style={{
              height: 10,
              width: w,
              borderRadius: 4,
              background: 'linear-gradient(90deg, #EDE7DF 25%, #E2DBD2 50%, #EDE7DF 75%)',
              backgroundSize: '520px 10px',
              animation: 'cornerShimmer 1.8s ease-in-out infinite',
            }}
          />
        ))}
      </div>
      {/* Animated dots */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: '#D46A3A',
              animation: `cornerDotPulse 1.4s ease-in-out ${i * 0.2}s infinite`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function LiveDocumentDraftingPanel({
  label,
  cotState,
}: {
  label?: string;
  cotState?: CoTState;
}) {
  const phases = cotState?.activityPhases ?? [];
  const hasLiveWork = phases.length > 0;
  const activeStep = cotState?.steps.find((step) => step.status === 'active');
  const latestError = [...(cotState?.steps ?? [])].reverse().find((step) => step.status === 'error');

  return (
    <div
      style={{
        width: 'min(760px, 100%)',
        minHeight: PAGE_HEIGHT,
        borderRadius: 4,
        padding: '42px 48px',
        backgroundColor: APP.bgDocSheet,
        boxShadow: APP.docSheetShadow,
        display: 'flex',
        flexDirection: 'column',
        gap: 22,
        fontFamily: APP.fontFamily,
      }}
    >
      <style>{SHIMMER_KEYFRAMES}</style>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase' as const,
              color: '#D46A3A',
              lineHeight: '16px',
            }}
          >
            Corner is writing
          </span>
          <span
            style={{
              fontSize: 22,
              fontWeight: 650,
              color: APP.textPrimary,
              lineHeight: '28px',
              overflowWrap: 'anywhere',
            }}
          >
            {label || 'Generating your document...'}
          </span>
          <span style={{ fontSize: 13, color: APP.textMuted, lineHeight: '19px', maxWidth: 560 }}>
            Live drafting notes will appear here while the document is being prepared.
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
            padding: '6px 10px',
            borderRadius: 999,
            backgroundColor: 'color-mix(in srgb, #D46A3A 12%, var(--app-bg-version-card))',
            color: '#D46A3A',
            fontSize: 11,
            fontWeight: 700,
            lineHeight: '14px',
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: '#D46A3A',
              animation: 'cornerDotPulse 1.4s ease-in-out infinite',
            }}
          />
          Live
        </div>
      </div>

      <div
        style={{
          borderRadius: 12,
          border: `1px solid ${APP.borderCard}`,
          backgroundColor: APP.bgCardBody,
          padding: 14,
          minHeight: 220,
          maxHeight: 430,
          overflow: 'auto',
          overscrollBehavior: 'contain',
        }}
      >
        {hasLiveWork ? (
          <CoTActivityCard phases={phases} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: APP.textPrimary }}>
              Starting the drafting pass...
            </span>
            {[260, 310, 238, 294, 180].map((w, i) => (
              <div
                key={i}
                style={{
                  height: 10,
                  width: w,
                  maxWidth: '100%',
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #EDE7DF 25%, #E2DBD2 50%, #EDE7DF 75%)',
                  backgroundSize: '520px 10px',
                  animation: 'cornerShimmer 1.8s ease-in-out infinite',
                }}
              />
            ))}
          </div>
        )}
      </div>

      {(activeStep || latestError) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 12px',
            borderRadius: 10,
            backgroundColor: latestError
              ? 'color-mix(in srgb, #D94545 10%, var(--app-bg-version-card))'
              : APP.bgContent,
            color: latestError ? '#B9382D' : APP.textMuted,
            fontSize: 12,
            lineHeight: '17px',
          }}
        >
          <span style={{ fontWeight: 700 }}>{latestError ? 'Needs attention:' : 'Now:'}</span>
          <span>{latestError?.title ?? activeStep?.title}</span>
        </div>
      )}
    </div>
  );
}

// ─── Split page strip (Paper 5U7-0) ────────────

function SplitPageStrip({ pageCount, currentPage }: { pageCount: number; currentPage?: number }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: 8,
        gap: 16,
      }}
    >
      {/* Header */}
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase' as const,
          color: APP.textSecondary,
          fontFamily: APP.fontFamily,
          lineHeight: '16px',
        }}
      >
        {pageCount} pages · each saved as a separate file
      </span>

      {/* Page thumbnails */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        {Array.from({ length: pageCount }, (_, i) => {
          const pageNum = i + 1;
          const isCurrent = pageNum === (currentPage ?? 1);
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              {/* Page thumbnail card */}
              <div
                style={{
                  width: 108,
                  height: 140,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 4,
                  padding: 12,
                  gap: 6,
                  backgroundColor: APP.bgVersionCard,
                  border: isCurrent ? '2px solid #D97645' : 'none',
                  boxShadow: isCurrent
                    ? '#D97645 0px 0px 0px 2px, #00000014 0px 2px 8px'
                    : '#00000014 0px 2px 8px',
                  flexShrink: 0,
                }}
              >
                {/* Fake content lines */}
                <div style={{ width: '60%', height: 6, borderRadius: 2, backgroundColor: '#2B2420' }} />
                <div style={{ width: '90%', height: 4, borderRadius: 2, backgroundColor: '#E4DDD4' }} />
                <div style={{ width: '80%', height: 4, borderRadius: 2, backgroundColor: '#E4DDD4' }} />
                <div style={{ width: '85%', height: 4, borderRadius: 2, backgroundColor: '#E4DDD4' }} />
                <div style={{ width: '50%', height: 4, borderRadius: 2, backgroundColor: '#E4DDD4' }} />
              </div>
              {/* Page label */}
              <span
                style={{
                  fontSize: 11,
                  fontWeight: isCurrent ? 600 : 400,
                  color: isCurrent ? APP.cornerGradientStart : APP.textSecondary,
                  fontFamily: APP.fontFamily,
                  lineHeight: '14px',
                }}
              >
                {isCurrent ? `Page ${pageNum} (current)` : `Page ${pageNum}`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Review Mode Bar (Paper 13b) ────────────────

function ReviewModeBar({
  stats,
  onExit,
  generating,
  generatingError,
}: {
  stats: { total: number; remaining: number };
  onExit: () => void;
  generating?: boolean;
  generatingError?: string | null;
}) {
  const isComplete = stats.total > 0 && stats.remaining === 0;
  const hasError = !!generatingError;

  // Determine bar colors based on state
  let barBg: string;
  let barBorder: string;
  let iconStroke: string;
  let labelColor: string;
  if (hasError) {
    barBg = '#FDF5F3';
    barBorder = '#F0D5CE';
    iconStroke = '#D97645';
    labelColor = '#C4654A';
  } else if (isComplete) {
    barBg = '#E9F2EC';
    barBorder = '#D5E3D9';
    iconStroke = APP.textReviewAccepted;
    labelColor = APP.textReviewAccepted;
  } else {
    barBg = APP.bgReviewBar;
    barBorder = APP.borderReviewBar;
    iconStroke = APP.textReviewPriorityHigh;
    labelColor = APP.textReviewPriorityHigh;
  }

  // Status text
  let statusText: string;
  if (hasError) {
    statusText = 'Generation failed';
  } else if (generating) {
    statusText = 'Generating suggestions...';
  } else if (isComplete) {
    statusText = 'Review complete · All suggestions reviewed';
  } else {
    statusText = `${stats.total} suggestions · ${stats.remaining} remaining`;
  }

  return (
    <div
      style={{
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        paddingInline: 16,
        backgroundColor: barBg,
        borderBottom: `1px solid ${barBorder}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Document icon */}
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
          <path d="M14 2v4a2 2 0 0 0 2 2h4" />
        </svg>
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: labelColor,
            lineHeight: '16px',
            fontFamily: APP.fontFamily,
          }}
        >
          {isComplete ? 'Review Complete' : 'Review Mode'}
        </span>
        <span style={{ fontSize: 12, fontWeight: 400, color: APP.textReviewMuted, lineHeight: '16px', fontFamily: APP.fontFamily }}>·</span>
        <span style={{ fontSize: 12, fontWeight: 400, color: APP.textCardDetail, lineHeight: '16px', fontFamily: APP.fontFamily }}>
          {statusText}
        </span>
      </div>

      <button
        type="button"
        onClick={onExit}
        style={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: 4,
          paddingBlock: 4,
          paddingInline: 10,
          backgroundColor: '#FDFCFB',
          border: `1px solid ${APP.borderReviewCardDefault}`,
          cursor: 'pointer',
          fontFamily: APP.fontFamily,
          fontSize: 11,
          fontWeight: 500,
          color: APP.textCardDetail,
          lineHeight: '14px',
        }}
      >
        {isComplete ? 'Done' : 'Exit Review'}
      </button>
    </div>
  );
}

// ─── Original / Revised Toggle ──────────────────

function DocViewToggle({
  view,
  onChange,
}: {
  view: 'original' | 'revised';
  onChange: (v: 'original' | 'revised') => void;
}) {
  const tabStyle = (active: boolean): React.CSSProperties => ({
    paddingBlock: 3,
    paddingInline: 10,
    backgroundColor: active ? APP.textDark : '#FDFCFB',
    color: active ? '#FFFFFF' : '#6B5E52',
    fontSize: 11,
    fontWeight: 500,
    fontFamily: APP.fontFamily,
    lineHeight: '14px',
    border: 'none',
    cursor: 'pointer',
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: 5,
        overflow: 'clip',
        border: `1px solid ${APP.borderReviewCardDefault}`,
      }}
    >
      <button type="button" onClick={() => onChange('original')} style={tabStyle(view === 'original')}>
        Original
      </button>
      <button type="button" onClick={() => onChange('revised')} style={tabStyle(view === 'revised')}>
        Revised
      </button>
    </div>
  );
}

// ─── Main component ─────────────────────────────

function partitionVersionLineage(versions: import('../../types').DocumentVersionSummary[]) {
  const main: typeof versions = [];
  const copies: typeof versions = [];
  for (const v of versions) {
    if (v.lineageKind === 'copy') copies.push(v);
    else main.push(v);
  }
  return { main, copies };
}

type VersionRailFolder = {
  id: string;
  name: string;
  expanded: boolean;
  parentId: string | null;
};

type VersionRailTreeState = {
  title?: string;
  folders: VersionRailFolder[];
  assignments: Record<string, string | null>;
  hiddenVersionIds?: string[];
};

function safeVersionTreeKey(versions: import('../../types').DocumentVersionSummary[], workspaceTitle?: string) {
  const projectId = versions.find((version) => version.projectId)?.projectId ?? workspaceTitle ?? 'workspace';
  return `corner.versionTree.${projectId}`;
}

function createVersionRailFolder(name = 'New folder'): VersionRailFolder {
  return {
    id: `folder-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name,
    expanded: true,
    parentId: null,
  };
}

function readVersionTreeState(key: string): VersionRailTreeState {
  if (typeof window === 'undefined') return { folders: [], assignments: {}, hiddenVersionIds: [] };
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return { folders: [], assignments: {}, hiddenVersionIds: [] };
    const parsed = JSON.parse(raw) as Partial<VersionRailTreeState>;
    return {
      title: typeof parsed.title === 'string' ? parsed.title : undefined,
      folders: Array.isArray(parsed.folders)
        ? parsed.folders
            .filter((folder): folder is VersionRailFolder => (
              Boolean(folder)
              && typeof folder.id === 'string'
              && typeof folder.name === 'string'
            ))
            .map((folder) => ({
              id: folder.id,
              name: folder.name,
              expanded: folder.expanded !== false,
              parentId: folder.parentId ?? null,
            }))
        : [],
      assignments: parsed.assignments && typeof parsed.assignments === 'object' ? parsed.assignments : {},
      hiddenVersionIds: Array.isArray(parsed.hiddenVersionIds)
        ? parsed.hiddenVersionIds.filter((id): id is string => typeof id === 'string')
        : [],
    };
  } catch {
    return { folders: [], assignments: {}, hiddenVersionIds: [] };
  }
}

function writeVersionTreeState(key: string, state: VersionRailTreeState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent('corner:version-tree-updated', { detail: { key } }));
  } catch {
    // Local organization is a convenience layer; storage failures should not block document work.
  }
}

function VersionTreeIcon({
  kind,
  mimeType,
  open,
}: { kind: 'folder' | 'file'; mimeType?: string; open?: boolean }) {
  if (kind === 'folder') {
    // Use a slightly different glyph for open vs. closed, like an IDE.
    const stroke = APP.textSecondary;
    return open ? (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 7.2C3 6.3 3.7 5.5 4.6 5.5h4.6L11 7.2h7.5c.9 0 1.6.8 1.6 1.7v.9H5.4c-.9 0-1.7.6-1.9 1.5L2 17.5c-.1.5.3 1 .8 1h15.3c.7 0 1.4-.5 1.6-1.2l1.7-6c.2-.7-.3-1.5-1.1-1.5" />
      </svg>
    ) : (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 7C3 6 3.8 5.2 4.8 5.2h5l2 2.2h7.4c1 0 1.8.8 1.8 1.8v7.6c0 1-.8 1.8-1.8 1.8H4.8c-1 0-1.8-.8-1.8-1.8V7Z" />
      </svg>
    );
  }
  // File: pick a flavour from mimeType so PDFs / DOCX / images get a faint tint.
  const lower = (mimeType ?? '').toLowerCase();
  const isPdf = /pdf/.test(lower);
  const isDoc = /word|officedocument|html/.test(lower);
  const isImg = /image\//.test(lower);
  const stroke = isPdf
    ? APP.bgPdfBadge
    : isDoc
      ? APP.bgDocBadge
      : isImg
        ? APP.bgImgBadge
        : APP.textSecondary;
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 3.5h8l4 4V20a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z" />
      <path d="M14 3.5V8h4" />
      {isPdf && <path d="M8 14h8M8 17h6" />}
      {isDoc && !isPdf && <path d="M8 13h8M8 16h8" />}
    </svg>
  );
}

/**
 * Single-character chevron used for folder expand/collapse. Sized in
 * a fixed slot so file rows align to the same indent as folder names.
 */
function VersionTreeChevron({ open }: { open: boolean }) {
  return (
    <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path
        d={open ? 'M2.5 4.2 L6 8 L9.5 4.2' : 'M4.5 2.5 L8 6 L4.5 9.5'}
        fill="none"
        stroke={APP.textSecondary}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Hover-revealed `⋯` action menu used by both folder and file rows in
 * the version-tree rail. Hoisted to module scope so React preserves
 * its hooks/state across `DocumentVersionRail` re-renders. The popover
 * is portaled to <body> so the rail's `overflow: hidden` and the tree
 * body's `overflow-y: auto` cannot clip it (a real bug on the previous
 * absolute-anchored implementation), and it flips above the trigger
 * when there's not enough space below.
 */
function VersionTreeActionMenu({
  isOpen,
  onToggle,
  onClose,
  onDelete,
  deleteLabel,
}: {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onDelete: () => void;
  deleteLabel: string;
}) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [popoverPos, setPopoverPos] = useState<{
    top: number;
    left: number;
    placement: 'below' | 'above';
  } | null>(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPopoverPos(null);
      return;
    }
    const btn = buttonRef.current;
    if (!btn) return;
    const computePos = () => {
      const rect = btn.getBoundingClientRect();
      const menuWidth = 168;
      const menuHeight = 44;
      const margin = 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      const placement: 'below' | 'above' =
        spaceBelow < menuHeight + margin ? 'above' : 'below';
      const top =
        placement === 'below' ? rect.bottom + 4 : rect.top - menuHeight - 4;
      let left = rect.right - menuWidth;
      if (left < margin) left = margin;
      if (left + menuWidth > window.innerWidth - margin) {
        left = window.innerWidth - menuWidth - margin;
      }
      setPopoverPos({ top, left, placement });
    };
    computePos();
    // Recompute on scroll/resize so the popover follows the trigger
    // (the tree body itself is scrollable). `true` listens in the
    // capture phase so we catch scroll events on inner containers,
    // not just window scroll.
    window.addEventListener('scroll', computePos, true);
    window.addEventListener('resize', computePos);
    return () => {
      window.removeEventListener('scroll', computePos, true);
      window.removeEventListener('resize', computePos);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current && buttonRef.current.contains(target)) return;
      if (popoverRef.current && popoverRef.current.contains(target)) return;
      onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="version-tree-action-button"
        onMouseDown={(event) => {
          // Stop the row's onClick (folder collapse / version select)
          // from firing when the user is opening the menu.
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onToggle();
        }}
        title="More actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        style={{
          width: 20,
          height: 20,
          border: 'none',
          borderRadius: 4,
          backgroundColor: isOpen ? APP.bgQuickAction : 'transparent',
          color: APP.textSecondary,
          cursor: 'pointer',
          fontSize: 14,
          lineHeight: '14px',
          padding: 0,
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isOpen ? 1 : 0,
        }}
      >
        ⋯
      </button>
      {isOpen && popoverPos
        ? createPortal(
            <div
              ref={popoverRef}
              role="menu"
              onMouseDown={(event) => event.stopPropagation()}
              style={{
                position: 'fixed',
                top: popoverPos.top,
                left: popoverPos.left,
                zIndex: 1000,
                minWidth: 168,
                padding: 4,
                borderRadius: 7,
                border: `1px solid ${APP.borderQuickAction}`,
                backgroundColor: APP.bgVersionCard,
                boxShadow: '0 12px 32px rgba(26,23,20,0.18)',
              }}
            >
              <button
                type="button"
                role="menuitem"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onDelete();
                }}
                onMouseEnter={(event) => {
                  (event.currentTarget as HTMLButtonElement).style.backgroundColor =
                    'color-mix(in srgb, currentColor 10%, transparent)';
                }}
                onMouseLeave={(event) => {
                  (event.currentTarget as HTMLButtonElement).style.backgroundColor =
                    'transparent';
                }}
                style={{
                  width: '100%',
                  border: 'none',
                  borderRadius: 5,
                  padding: '8px 10px',
                  backgroundColor: 'transparent',
                  color: APP.textReviewPriorityHigh,
                  cursor: 'pointer',
                  fontFamily: APP.fontFamily,
                  fontSize: 12,
                  fontWeight: 600,
                  textAlign: 'left',
                }}
              >
                {deleteLabel}
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function DocumentVersionRail({
  versions,
  logicalDocumentId,
  activeVersionId,
  verifyResult,
  onSelectVersion,
  onVerifyVersion,
  peekKey,
  workspaceTitle,
  onRenameWorkspace,
}: {
  versions: import('../../types').DocumentVersionSummary[];
  logicalDocumentId?: string | null;
  activeVersionId?: string | null;
  verifyResult?: import('../../types').VersionVerificationResult | null;
  onSelectVersion?: (version: import('../../types').DocumentVersionSummary) => void;
  onVerifyVersion?: (baseVersionId: string) => void;
  /** When incremented, briefly expands the rail (if collapsed) then collapses after the width transition. */
  peekKey?: number;
  workspaceTitle?: string;
  /**
   * Renames the project. Wired up to the same backend rename API that
   * the project banner uses, so the master folder name in the rail
   * stays in sync with the title shown above. When omitted the
   * pencil-rename UI is hidden entirely (no point letting the user
   * edit a name that has nowhere to go).
   */
  onRenameWorkspace?: (next: string) => void;
}) {
  const [expanded, setExpanded] = useState(() => versions.length === 0);
  const [treeState, setTreeState] = useState<VersionRailTreeState>({ folders: [], assignments: {}, hiddenVersionIds: [] });
  const [renamingTitle, setRenamingTitle] = useState(false);
  // The rail's master folder name is always the project title — the
  // rail no longer keeps a private localStorage override. The draft is
  // seeded from `workspaceTitle` whenever it changes (banner edit) or
  // when the user opens the inline rename input.
  const [titleDraft, setTitleDraft] = useState(workspaceTitle ?? 'Project');
  const [openActionMenu, setOpenActionMenu] = useState<{ kind: 'folder' | 'file'; id: string } | null>(null);
  const storageKey = safeVersionTreeKey(versions, workspaceTitle);
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const railRef = useRef<HTMLDivElement>(null);
  const lastProcessedPeekKeyRef = useRef(0);
  const peekAutoCollapseRef = useRef(false);
  const skipNextTreeWriteRef = useRef(false);

  useLayoutEffect(() => {
    if (!peekKey || peekKey === lastProcessedPeekKeyRef.current) return;
    lastProcessedPeekKeyRef.current = peekKey;
    if (expandedRef.current) {
      peekAutoCollapseRef.current = false;
      return;
    }
    peekAutoCollapseRef.current = true;
    setExpanded(true);
  }, [peekKey]);

  useEffect(() => {
    const el = railRef.current;
    if (!el || !peekAutoCollapseRef.current || !expanded) return;
    const onEnd = (e: TransitionEvent) => {
      if (e.target !== el || e.propertyName !== 'width') return;
      if (!peekAutoCollapseRef.current) return;
      peekAutoCollapseRef.current = false;
      setExpanded(false);
    };
    el.addEventListener('transitionend', onEnd);
    return () => el.removeEventListener('transitionend', onEnd);
  }, [expanded, peekKey]);

  const toggleExpanded = () => {
    peekAutoCollapseRef.current = false;
    setExpanded((next) => !next);
  };

  const versionIds = versions.map((version) => version.id).join('|');

  useEffect(() => {
    const next = readVersionTreeState(storageKey);
    const knownVersionIds = new Set(versions.map((version) => version.id));
    const knownFolderIds = new Set(next.folders.map((folder) => folder.id));
    const assignments = Object.fromEntries(
      Object.entries(next.assignments).filter(([versionId, folderId]) => (
        knownVersionIds.has(versionId) && (!folderId || knownFolderIds.has(folderId))
      )),
    );
    const hiddenVersionIds = (next.hiddenVersionIds ?? []).filter((id) => knownVersionIds.has(id));
    skipNextTreeWriteRef.current = true;
    // We intentionally drop any persisted `next.title` — the project
    // title is the single source of truth and lives on the server. If
    // an old localStorage entry still has a stale rail-only title it
    // gets silently ignored from here on.
    setTreeState({ ...next, title: undefined, assignments, hiddenVersionIds });
    setTitleDraft(workspaceTitle ?? 'Project');
  }, [storageKey, versionIds, workspaceTitle]);

  useEffect(() => {
    if (skipNextTreeWriteRef.current) {
      skipNextTreeWriteRef.current = false;
      return;
    }
    const knownVersionIds = new Set(versions.map((version) => version.id));
    const knownFolderIds = new Set(treeState.folders.map((folder) => folder.id));
    const assignments = Object.fromEntries(
      Object.entries(treeState.assignments).filter(([versionId, folderId]) => (
        knownVersionIds.has(versionId) && (!folderId || knownFolderIds.has(folderId))
      )),
    );
    const hiddenVersionIds = (treeState.hiddenVersionIds ?? []).filter((id) => knownVersionIds.has(id));
    writeVersionTreeState(storageKey, { ...treeState, assignments, hiddenVersionIds });
  }, [storageKey, treeState, versionIds]);

  const hiddenVersionIds = new Set(treeState.hiddenVersionIds ?? []);
  const hasCanonicalLogicalDocument = Boolean(
    logicalDocumentId
      && !String(logicalDocumentId).startsWith('local:')
      && !String(logicalDocumentId).startsWith('conversation:'),
  );
  const lineageVersions = hasCanonicalLogicalDocument
    ? versions.filter((version) => !version.projectId || String(version.projectId) === String(logicalDocumentId))
    : versions;
  const visibleVersions = lineageVersions.filter((version) => !hiddenVersionIds.has(version.id));
  const active = visibleVersions.find((version) => version.id === activeVersionId) ?? visibleVersions.at(-1) ?? visibleVersions[0];
  const rootTitle = workspaceTitle || 'Project';
  useEffect(() => {
    if (!logicalDocumentId) return;
    const mismatched = versions.filter((version) => (
      Boolean(version.projectId) && String(version.projectId) !== String(logicalDocumentId)
    ));
    if (mismatched.length === 0) return;
    console.error('[DocumentVersionRail] Version-to-group mismatch detected', {
      logicalDocumentId,
      mismatchedVersionIds: mismatched.map((v) => v.id),
      mismatchedProjectIds: [...new Set(mismatched.map((v) => v.projectId).filter(Boolean))],
      action: 'excluded-from-visible-lineage',
    });
  }, [logicalDocumentId, versions]);
  const rootVersions = visibleVersions.filter((version) => !treeState.assignments[version.id]);
  const versionsByFolder = (folderId: string) => visibleVersions.filter((version) => treeState.assignments[version.id] === folderId);
  const childFolders = (parentId: string | null) => treeState.folders.filter((folder) => folder.parentId === parentId);

  const isDescendantFolder = (candidateId: string, possibleAncestorId: string): boolean => {
    const candidate = treeState.folders.find((folder) => folder.id === candidateId);
    if (!candidate?.parentId) return false;
    if (candidate.parentId === possibleAncestorId) return true;
    return isDescendantFolder(candidate.parentId, possibleAncestorId);
  };

  const assignVersionToFolder = (versionId: string, folderId: string | null) => {
    setTreeState((current) => ({
      ...current,
      assignments: { ...current.assignments, [versionId]: folderId },
    }));
  };

  const handleDropVersion = (event: React.DragEvent, folderId: string | null) => {
    event.preventDefault();
    const versionId = event.dataTransfer.getData('application/x-corner-version');
    const draggedFolderId = event.dataTransfer.getData('application/x-corner-version-folder');
    if (versionId) {
      assignVersionToFolder(versionId, folderId);
      return;
    }
    if (!draggedFolderId || draggedFolderId === folderId) return;
    if (folderId && isDescendantFolder(folderId, draggedFolderId)) return;
    updateFolder(draggedFolderId, { parentId: folderId });
  };

  const commitTitle = () => {
    const nextTitle = titleDraft.trim();
    setRenamingTitle(false);
    if (!nextTitle || nextTitle === (workspaceTitle ?? 'Project')) {
      setTitleDraft(workspaceTitle ?? 'Project');
      return;
    }
    setTitleDraft(nextTitle);
    onRenameWorkspace?.(nextTitle);
  };

  const createFolder = (parentId: string | null = null) => {
    const folder = createVersionRailFolder();
    folder.parentId = parentId;
    setTreeState((current) => ({ ...current, folders: [...current.folders, folder] }));
  };

  const updateFolder = (folderId: string, patch: Partial<VersionRailFolder>) => {
    setTreeState((current) => ({
      ...current,
      folders: current.folders.map((folder) => (folder.id === folderId ? { ...folder, ...patch } : folder)),
    }));
  };

  const hideVersion = (versionId: string) => {
    setTreeState((current) => ({
      ...current,
      hiddenVersionIds: [...new Set([...(current.hiddenVersionIds ?? []), versionId])],
      assignments: Object.fromEntries(Object.entries(current.assignments).filter(([id]) => id !== versionId)),
    }));
    setOpenActionMenu(null);
  };

  const deleteFolder = (folderId: string) => {
    setTreeState((current) => {
      const collect = (id: string): string[] => [
        id,
        ...current.folders
          .filter((folder) => folder.parentId === id)
          .flatMap((folder) => collect(folder.id)),
      ];
      const deletedFolderIds = new Set(collect(folderId));
      const hiddenFromFolders = Object.entries(current.assignments)
        .filter(([, assignedFolderId]) => assignedFolderId && deletedFolderIds.has(assignedFolderId))
        .map(([versionId]) => versionId);
      return {
        ...current,
        folders: current.folders.filter((folder) => !deletedFolderIds.has(folder.id)),
        assignments: Object.fromEntries(
          Object.entries(current.assignments).filter(([, assignedFolderId]) => !assignedFolderId || !deletedFolderIds.has(assignedFolderId)),
        ),
        hiddenVersionIds: [...new Set([...(current.hiddenVersionIds ?? []), ...hiddenFromFolders])],
      };
    });
    setOpenActionMenu(null);
  };

  // Helper that builds the props for `<VersionTreeActionMenu />` from
  // the current `openActionMenu` state. Inlining this in the two row
  // renderers (instead of wrapping `VersionTreeActionMenu` in another
  // inner component) is intentional — wrapping breaks hooks state
  // because the wrapper's function reference changes on every parent
  // render, which would unmount/remount the popover each render and
  // wipe its position/refs.
  const actionMenuPropsFor = (
    kind: 'folder' | 'file',
    id: string,
    onDelete: () => void,
    deleteLabel: string,
  ) => ({
    isOpen: openActionMenu?.kind === kind && openActionMenu.id === id,
    onToggle: () =>
      setOpenActionMenu((current) =>
        current?.kind === kind && current.id === id ? null : { kind, id },
      ),
    onClose: () => setOpenActionMenu(null),
    onDelete,
    deleteLabel,
  });

  const renderVersionRow = (version: import('../../types').DocumentVersionSummary) => {
    const isActive = active ? version.id === active.id : false;
    const selectable = Boolean(version.fileId);
    const isSignedVersion = version.source === 'signed';
    const sublabel = isEditableCopyFromSignedPdfVersion(version)
      ? 'Editable copy from signed PDF'
      : version.lineageKind === 'copy'
      ? 'copy'
      : isSignedVersion
        ? 'Signed'
        : version.source.replace('_', ' ');
    // IDE-style row: 24px tall, no per-row border/background.
    // Hover background + accent left-bar come from the
    // `.version-tree-row` rule in the rail's <style> block.
    const baseLabel = version.fileName || `Version ${version.versionNumber}`;
    const versionTag = `v${version.versionNumber}`;
    return (
      <div
        key={version.id}
        className={`version-tree-row${isActive ? ' is-active' : ''}${!selectable ? ' is-disabled' : ''}`}
        role="button"
        tabIndex={selectable ? 0 : -1}
        onClick={() => {
          if (!selectable) return;
          docVersionDebugLog('row_click', {
            documentId: version.projectId ?? logicalDocumentId ?? null,
            versionId: version.id,
            fileId: version.fileId ?? null,
            type: version.source ?? null,
            versionLabel: `v${version.versionNumber ?? '?'}`,
          });
          onSelectVersion?.(version);
        }}
        onKeyDown={(event) => {
          if (!selectable) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            docVersionDebugLog('row_click', {
              documentId: version.projectId ?? logicalDocumentId ?? null,
              versionId: version.id,
              fileId: version.fileId ?? null,
              type: version.source ?? null,
              versionLabel: `v${version.versionNumber ?? '?'}`,
              via: 'keyboard',
            });
            onSelectVersion?.(version);
          }
        }}
        draggable
        onDragStart={(event) => {
          event.dataTransfer.setData('application/x-corner-version', version.id);
          event.dataTransfer.effectAllowed = 'move';
        }}
        title={selectable ? `${baseLabel} · ${sublabel}` : `${baseLabel} (in progress)`}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height: 24,
          padding: '0 6px 0 8px',
          cursor: selectable ? 'pointer' : 'default',
          opacity: selectable ? 1 : 0.6,
          textAlign: 'left',
          fontFamily: APP.fontFamily,
          width: '100%',
          borderRadius: 4,
          color: APP.textPrimary,
        }}
      >
        {/* Indent slot kept blank for alignment with the chevron column on folders */}
        <span style={{ width: 10, flexShrink: 0 }} aria-hidden />
        <VersionTreeIcon kind="file" mimeType={version.mimeType} />
        <span
          style={{
            minWidth: 0,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 12,
            fontWeight: isActive ? 650 : 500,
            lineHeight: '24px',
          }}
        >
          {baseLabel}
        </span>
        {isSignedVersion && (
          <span
            title="Signed version"
            style={{
              flexShrink: 0,
              borderRadius: 999,
              backgroundColor: 'rgba(27, 122, 75, 0.10)',
              color: '#1B7A4B',
              border: '1px solid rgba(27, 122, 75, 0.20)',
              padding: '0 5px',
              fontSize: 9,
              fontWeight: 700,
              lineHeight: '13px',
            }}
          >
            Signed
          </span>
        )}
        <span
          className="version-tree-row-meta"
          style={{
            flexShrink: 0,
            fontSize: 10,
            fontWeight: 600,
            color: APP.textMuted,
            lineHeight: '24px',
            letterSpacing: '0.02em',
          }}
        >
          {versionTag}
        </span>
        <VersionTreeActionMenu
          {...actionMenuPropsFor(
            'file',
            version.id,
            () => hideVersion(version.id),
            'Remove from tree',
          )}
        />
      </div>
    );
  };

  const verifyBaseId = (partitionVersionLineage(visibleVersions).main[0] ?? visibleVersions[0])?.id;
  const renderDropZone = (folderId: string | null, children: React.ReactNode) => (
    <div
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => handleDropVersion(event, folderId)}
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      {children}
    </div>
  );

  const renderFolderRow = (folder: VersionRailFolder): React.ReactNode => {
    const folderVersions = versionsByFolder(folder.id);
    const nestedFolders = childFolders(folder.id);
    return (
      <div key={folder.id} style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          className="version-tree-folder-row"
          draggable
          onDragStart={(event) => {
            event.dataTransfer.setData('application/x-corner-version-folder', folder.id);
            event.dataTransfer.effectAllowed = 'move';
          }}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => handleDropVersion(event, folder.id)}
          onClick={() => updateFolder(folder.id, { expanded: !folder.expanded })}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            height: 24,
            padding: '0 6px 0 4px',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              updateFolder(folder.id, { expanded: !folder.expanded });
            }}
            style={{
              border: 'none',
              background: 'transparent',
              color: APP.textSecondary,
              cursor: 'pointer',
              padding: 0,
              width: 14,
              height: 14,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            title={folder.expanded ? 'Collapse folder' : 'Expand folder'}
          >
            <VersionTreeChevron open={folder.expanded} />
          </button>
          <VersionTreeIcon kind="folder" open={folder.expanded} />
          <input
            value={folder.name}
            onChange={(event) => updateFolder(folder.id, { name: event.target.value })}
            onBlur={() => {
              if (!folder.name.trim()) updateFolder(folder.id, { name: 'New folder' });
            }}
            onClick={(event) => event.stopPropagation()}
            draggable={false}
            style={{
              minWidth: 0,
              flex: 1,
              border: 'none',
              background: 'transparent',
              color: APP.textPrimary,
              fontFamily: APP.fontFamily,
              fontSize: 12,
              fontWeight: 600,
              outline: 'none',
              padding: 0,
              lineHeight: '24px',
              cursor: 'text',
            }}
          />
          <button
            type="button"
            className="version-tree-action-button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              createFolder(folder.id);
              updateFolder(folder.id, { expanded: true });
            }}
            title="New folder inside"
            style={{
              width: 18,
              height: 18,
              border: 'none',
              borderRadius: 4,
              backgroundColor: 'transparent',
              color: APP.textSecondary,
              cursor: 'pointer',
              fontSize: 14,
              lineHeight: '14px',
              padding: 0,
              opacity: 0,
            }}
          >
            +
          </button>
          <VersionTreeActionMenu
            {...actionMenuPropsFor(
              'folder',
              folder.id,
              () => deleteFolder(folder.id),
              'Remove folder',
            )}
          />
        </div>
        {folder.expanded && (
          // Indent children and draw a vertical guide line — VS-Code-style.
          <div style={{ position: 'relative', paddingLeft: 14 }}>
            <div
              aria-hidden
              style={{
                position: 'absolute',
                left: 11,
                top: 2,
                bottom: 2,
                width: 1,
                background: APP.borderQuickAction,
                opacity: 0.6,
              }}
            />
            {folderVersions.map((version) => renderVersionRow(version))}
            {nestedFolders.map((child) => renderFolderRow(child))}
            {folderVersions.length === 0 && nestedFolders.length === 0 && (
              <div
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => handleDropVersion(event, folder.id)}
                style={{
                  height: 22,
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: 18,
                  color: APP.textMuted,
                  fontFamily: APP.fontFamily,
                  fontSize: 11,
                  fontStyle: 'italic',
                }}
              >
                Empty folder
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={railRef}
      style={{
        // Slightly wider expanded — tree rows breathe better at 256.
        // Collapsed state stays a slim IDE-style activity-bar rail.
        width: expanded ? 256 : 36,
        height: '100%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: `1px solid ${APP.border}`,
        backgroundColor: APP.bgVersionCanvas,
        transition: 'width 0.16s ease',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <style>
        {`
          .version-tree-row,
          .version-tree-folder-row {
            transition: background-color 80ms ease;
            user-select: none;
          }
          .version-tree-row:hover:not(.is-disabled),
          .version-tree-folder-row:hover {
            background-color: var(--app-bg-history-active);
          }
          .version-tree-row.is-active {
            background-color: color-mix(in srgb, ${APP.cornerGradientStart} 20%, transparent);
          }
          .version-tree-row.is-active::before {
            content: '';
            position: absolute;
            left: 0;
            top: 4px;
            bottom: 4px;
            width: 2px;
            border-radius: 1px;
            background: ${APP.cornerGradientStart};
          }
          .version-tree-row:hover .version-tree-action-button,
          .version-tree-folder-row:hover .version-tree-action-button {
            opacity: 1 !important;
          }
          .version-tree-row:focus-visible,
          .version-tree-folder-row:focus-visible {
            outline: 1px solid ${APP.cornerGradientStart};
            outline-offset: -1px;
          }
          .version-tree-rail-collapse-btn:hover {
            background-color: var(--app-bg-history-active);
          }
          .version-tree-rail-header-action:hover {
            background-color: var(--app-bg-history-active);
            color: ${APP.textPrimary};
          }
        `}
      </style>

      {!expanded ? (
        // Collapsed: thin activity-bar with a single expand button + version pip.
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8, gap: 6 }}>
          <button
            type="button"
            className="version-tree-rail-collapse-btn"
            onClick={toggleExpanded}
            title="Show files"
            aria-label="Show files"
            style={{
              width: 26,
              height: 26,
              border: 'none',
              borderRadius: 5,
              background: 'transparent',
              color: APP.textSecondary,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 6.5C3 5.7 3.7 5 4.5 5h4.6L11 6.7h7.5c.8 0 1.5.7 1.5 1.5v9.3c0 .8-.7 1.5-1.5 1.5h-14C3.7 19 3 18.3 3 17.5V6.5Z" />
            </svg>
          </button>
          {active ? (
            <div
              title={`Active version: v${active.versionNumber}`}
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                color: APP.textSecondary,
                fontFamily: APP.fontFamily,
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                letterSpacing: '0.05em',
                marginTop: 4,
              }}
            >
              v{active.versionNumber}
            </div>
          ) : null}
        </div>
      ) : (
        <>
          {/* IDE header strip: small caps title + collapse/new-folder actions. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              minWidth: 0,
              padding: '8px 6px 6px 10px',
              borderBottom: `1px solid ${APP.border}`,
              backgroundColor: APP.bgVersionCanvas,
            }}
          >
            <button
              type="button"
              onClick={toggleExpanded}
              className="version-tree-rail-header-action"
              title="Hide files"
              aria-label="Hide files"
              style={{
                width: 18,
                height: 18,
                border: 'none',
                borderRadius: 3,
                background: 'transparent',
                color: APP.textMuted,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                flexShrink: 0,
              }}
            >
              <VersionTreeChevron open />
            </button>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontFamily: APP.fontFamily,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: APP.textMuted,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={rootTitle}
            >
              {rootTitle}
            </span>
            <button
              type="button"
              onClick={() => createFolder(null)}
              className="version-tree-rail-header-action"
              title="New folder"
              aria-label="New folder"
              style={{
                width: 22,
                height: 22,
                borderRadius: 4,
                border: 'none',
                backgroundColor: 'transparent',
                color: APP.textSecondary,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 7C3 6 3.8 5.2 4.8 5.2h5l2 2.2h7.4c1 0 1.8.8 1.8 1.8v7.6c0 1-.8 1.8-1.8 1.8H4.8c-1 0-1.8-.8-1.8-1.8V7Z" />
                <path d="M12 12v6M9 15h6" />
              </svg>
            </button>
            {onRenameWorkspace && (
              <button
                type="button"
                onClick={() => {
                  setTitleDraft(workspaceTitle ?? 'Project');
                  setRenamingTitle(true);
                }}
                className="version-tree-rail-header-action"
                title="Rename project"
                aria-label="Rename project"
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 4,
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: APP.textSecondary,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
                </svg>
              </button>
            )}
          </div>

          {renamingTitle && (
            <div style={{ padding: '6px 8px', borderBottom: `1px solid ${APP.border}` }}>
              <input
                value={titleDraft}
                autoFocus
                onChange={(event) => setTitleDraft(event.target.value)}
                onBlur={commitTitle}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') commitTitle();
                  if (event.key === 'Escape') {
                    setTitleDraft(rootTitle);
                    setRenamingTitle(false);
                  }
                }}
                placeholder="Project name"
                style={{
                  width: '100%',
                  border: `1px solid ${APP.cornerGradientStart}`,
                  borderRadius: 4,
                  padding: '3px 6px',
                  backgroundColor: APP.bgVersionCard,
                  color: APP.textPrimary,
                  fontFamily: APP.fontFamily,
                  fontSize: 12,
                  fontWeight: 600,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}

          {/* Tree body */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              minHeight: 0,
              padding: '4px 4px 8px',
              flex: 1,
            }}
          >
            {renderDropZone(null, (
              <>
                {rootVersions.map((version) => renderVersionRow(version))}
                {rootVersions.length === 0 && childFolders(null).length === 0 && (
                  <div
                    style={{
                      padding: '14px 10px',
                      color: APP.textMuted,
                      fontFamily: APP.fontFamily,
                      fontSize: 11,
                      lineHeight: '15px',
                      textAlign: 'left',
                    }}
                  >
                    No files yet — new documents and versions will show up here.
                  </div>
                )}
              </>
            ))}
            {childFolders(null).map((folder) => renderFolderRow(folder))}
          </div>

          {/* Footer actions */}
          {(versions.length > 1 && verifyBaseId) || verifyResult ? (
            <div
              style={{
                borderTop: `1px solid ${APP.border}`,
                padding: '6px 8px 8px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                backgroundColor: APP.bgVersionCanvas,
              }}
            >
              {versions.length > 1 && verifyBaseId && (
                <button
                  type="button"
                  onClick={() => onVerifyVersion?.(verifyBaseId)}
                  style={{
                    width: '100%',
                    border: `1px solid ${APP.borderQuickAction}`,
                    borderRadius: 5,
                    padding: '5px 8px',
                    backgroundColor: 'transparent',
                    color: APP.textPrimary,
                    cursor: 'pointer',
                    fontFamily: APP.fontFamily,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  Verify changes
                </button>
              )}
              {verifyResult && (
                <div
                  style={{
                    borderRadius: 5,
                    borderLeft: `2px solid ${verifyResult.unexpectedChanges.length ? '#D97645' : '#4DB96C'}`,
                    backgroundColor: 'transparent',
                    padding: '4px 8px',
                    color: APP.textPrimary,
                    fontFamily: APP.fontFamily,
                    fontSize: 11,
                    lineHeight: '15px',
                  }}
                >
                  <strong>{verifyResult.summary}</strong>
                  <div style={{ marginTop: 2, color: APP.textSecondary, fontSize: 10.5 }}>
                    {verifyResult.changedSections.length} text change{verifyResult.changedSections.length === 1 ? '' : 's'} detected.
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

interface DocumentColumnProps {
  fileName?: string;
  filePreviewUrl?: string | null;
  /** Stable document key for the placeholder values store (live overlay).
   *  Falls back to the preview URL when omitted. */
  documentId?: string;
  /** When true, the PDF page fills the full column width (signing/presenting mode). */
  presentingMode?: boolean;
  folders?: import('../../types').Folder[];
  currentFolderId?: string | null;
  onAddToFolder?: (folderId: string) => void;
  onCreateFolder?: (name: string) => Promise<string> | string;
  /** Toggle a folder's `kind` between `'general'` and `'contracts'`. When provided, the folder picker shows a "+ Contracts" pill on each row so users can mark folders as part of the contract velocity knowledge base. */
  onToggleFolderContractsKind?: (folderId: string, nextKind: 'general' | 'contracts') => void;
  onDownload?: () => void;
  onDownloadDocx?: () => void;
  /** When true, renders image comparison view instead of doc viewer */
  imageMode?: boolean;
  /** Original image dimensions string, e.g. "2048 × 2560" */
  imageDimensions?: string;
  /** Original image file size, e.g. "3.2 MB" */
  imageFileSize?: string;
  /** Result of an image transformation */
  imageResult?: ImageResult | null;
  /** Action chip click handler (image mode) */
  onImageActionClick?: (label: string) => void;
  /** Label for the document variant pill (e.g. "Original PDF"). Hidden when absent. */
  variantLabel?: string;
  /** Called when a variant dropdown action is selected. */
  onVariantAction?: (action: 'retrieve_original' | 'retrieve_with_prompt') => void;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  documentVersions?: import('../../types').DocumentVersionSummary[];
  versionWorkspaceTitle?: string;
  /**
   * Renames the workspace project. Plumbed straight through to the
   * version rail's master-folder pencil-rename so the rail and the
   * project banner share one source of truth (the server).
   */
  onRenameWorkspace?: (next: string) => void;
  activeLogicalDocumentId?: string | null;
  activeVersionId?: string | null;
  versionVerifyResult?: import('../../types').VersionVerificationResult | null;
  onSelectDocumentVersion?: (version: import('../../types').DocumentVersionSummary) => void;
  onVerifyDocumentVersion?: (baseVersionId: string) => void;
  onVersionHistory?: () => void;
  /** Bump to play a one-shot expand → collapse hint on the desktop version rail. */
  versionRailPeekKey?: number;
  /** When non-null, show processing overlay on the document viewer */
  processing?: { progress: number; label: string } | null;
  /** Live CoT stream shown in the document pane while generated docs are being drafted. */
  cotState?: CoTState;
  /** E-sign mode: signature fields to overlay on the document */
  esignFields?: SignatureField[];
  /** Total PDF page count (for "Page X of Y" header) */
  esignPageCount?: number;
  /** User's display name for signed fields */
  esignSignerName?: string;
  /** Called when an awaiting signature field is clicked */
  onSignFieldClick?: (field: SignatureField) => void;
  /** Index of the active signature field in field-by-field advancing mode */
  esignActiveFieldIndex?: number;
  /** Called when the "Next" banner is clicked in field-by-field advancing */
  onEsignNextField?: () => void;
  /** Template-memory: when set, surfaces the SaveToTemplatePrompt
   *  banner after each field drag. Display name only; the workflow +
   *  template ids are baked into the onSaveFieldToTemplate callback. */
  linkedTemplateName?: string;
  /** Template-memory: persist a moved field's bbox to the linked
   *  template. Caller (App.tsx) closes over (workflowId, templateId)
   *  to call patchWorkflowTemplateField. */
  onSaveFieldToTemplate?: (input: {
    fieldId: string;
    rect: { x: number; y: number; width: number; height: number };
  }) => void | Promise<void>;
  // ─── v1 placement-redesign: human-first marker editing ─────────────
  /** Forces the embedded PdfSigningViewer into 'configure' mode where
   *  the new placement UX (per-signer colors, + buttons, × delete,
   *  legend sidebar) is active. Defaults to 'sign' for back-compat. */
  esignViewerMode?: 'sign' | 'configure';
  /** Recipient list shown in the legend + used to color markers. */
  esignSigners?: Array<{ email: string; name: string; role: string }>;
  /** Index into `esignSigners` — new placements attach to this signer. */
  esignActiveSignerIndex?: number;
  onSelectSigner?: (idx: number) => void;
  /** Shared placement mode controlled by the chat guide and toolbar. */
  esignPlacementMode?: PlacementType | null;
  onEsignPlacementModeChange?: (mode: PlacementType | null) => void;
  /** Append a freshly-placed marker to the working set. */
  onAddField?: (field: SignatureField) => void;
  /** Remove a marker (× confirm path). */
  onDeleteField?: (fieldId: string) => void;
  /** Flip a marker's state to 'confirmed' after a drag. */
  onConfirmField?: (fieldId: string) => void;
  /** Persist a drag move to the working set. */
  onMoveField?: (fieldId: string, rect: { x: number; y: number; width: number; height: number }) => void;
  /** Final self-sign action after the user has reviewed/placed markers. */
  onApplySignature?: () => void;
  applySignatureDisabled?: boolean;
  onSendForSignature?: () => void;
  sendForSignatureDisabled?: boolean;
  /** Add recipient email from PdfSigningViewer legend (updates signer list). */
  onAddSigningRecipientEmail?: (email: string) => void;
  /**
   * When true (default), + Signature / + Date / … stay on the PDF toolbar.
   * When false, those controls are omitted — parent drives placement from chat
   * (main esign workspace with SigningGuideCard).
   */
  esignInlinePlacementToolbar?: boolean;
  /**
   * Doc top-bar "Send for signature" entry point. Clicking this is the
   * canonical way for a user to start the signing flow on the doc they
   * are currently looking at. The handler is owned by App.tsx and
   * decides whether to enter mode='esign' (for PDFs), nudge a save-as-
   * PDF (for HTML drafts), or refuse (for unsupported file types).
   *
   * `signingButtonState` controls the rendered look:
   *  - 'available' → primary green CTA (the doc is ready to sign)
   *  - 'draft'     → secondary amber CTA (converts the HTML draft to PDF)
   *  - 'convertible' → secondary amber CTA (converts DOCX/TXT to PDF)
   *  - 'unsupported' → secondary gray CTA (visible but advises convert-to-PDF)
   *  - 'in-flight' or 'hidden' → button is not rendered
   */
  onLaunchSigning?: () => void;
  signingButtonState?: 'hidden' | 'available' | 'draft' | 'convertible' | 'unsupported' | 'in-flight';
  /** When > 0, show the split page strip overlay in the document viewer */
  splitPageCount?: number;
  /** When set, show OCR confidence + word count badge on the document (Paper 5XP) */
  ocrOverlay?: { confidence: number; wordCount: number } | null;
  /** When set, show QR code preview in the document viewer (Paper 64J-0) */
  qrPreview?: { imageUrl: string; payload: string; format: string; sizePx: number } | null;
  // ─── Review mode (Paper 13b) ──────────────────
  reviewMode?: boolean;
  reviewSuggestions?: ReviewSuggestion[];
  reviewActiveSuggestionId?: string | null;
  reviewStats?: { total: number; remaining: number };
  onExitReview?: () => void;
  onReviewAnnotationClick?: (id: string) => void;
  reviewDocumentView?: 'original' | 'revised';
  onReviewDocumentViewChange?: (view: 'original' | 'revised') => void;
  reviewUndoEntry?: { sectionRef: string; action: 'accepted' | 'dismissed' } | null;
  onReviewUndo?: () => void;
  reviewGenerating?: boolean;
  reviewGeneratingError?: string | null;
  // ─── Risk snapshot ──────────────────────────
  /** Compact risk badge shown in the document header */
  riskSnapshot?: {
    nudgeLevel: 'attention' | 'reconsider';
    label: string;
    laneKey?: string | null;
    jurisdiction?: string | null;
  } | null;
  /** Called when user clicks the risk badge */
  onRiskSnapshotClick?: () => void;
  // ─── Draft/editing mode ──────────────────────
  draftMode?: boolean;
  draftContent?: string;
  /**
   * True while an AI draft stream is currently writing into draftContent.
   * When true the document column renders the read-only paginated view
   * (no TipTap editor) so overflow-driven page splitting handles the layout
   * instead of in-editor padding spacers — fixes content bleeding into
   * page gaps during streaming.
   */
  draftIsStreaming?: boolean;
  // ─── Collaboration ────────────────────────────
  /** Presence users currently viewing this document */
  presenceUsers?: import('@corner/shared').PresenceUser[];
  /** Remote cursors from other collaborators */
  remoteCursors?: Map<string, import('@corner/shared').CursorPosition>;
  /** Called when user wants to open the Share modal */
  onShare?: () => void;
  /** Called when cursor moves over the document (for sending to other users) */
  onCursorMove?: (x: number, y: number, pageNumber: number) => void;
  /** Number of comment threads on this document */
  commentCount?: number;
  /** Comment pins to render on the document */
  commentPins?: import('@corner/shared').CommentPinData[];
  /** Currently active comment pin */
  activeCommentPinId?: string | null;
  /** Called when a comment pin is clicked */
  onCommentPinClick?: (pinId: string) => void;
  /** Called when a comment pin is dragged to a new position */
  onCommentPinMove?: (pinId: string, xPercent: number, yPercent: number) => void;
  /** Called when user clicks on the document to place a new comment */
  onDocumentClick?: (xPercent: number, yPercent: number, pageNumber: number) => void;
  /** Whether comment mode is active (clicks place comments instead of editing) */
  commentMode?: boolean;
  /** Toggle comment mode on/off */
  onCommentModeChange?: (active: boolean) => void;
  /** Whether reader view (text mode) is active */
  readerView?: boolean;
  /** Toggle reader view on/off */
  onReaderViewChange?: (active: boolean) => void;
  /** Extracted document text for reader view */
  documentText?: {
    paragraphs: string[];
    loading: boolean;
    error?: string | null;
    blockAnchors?: { page: number; paragraphIndex: number }[];
  } | null;
  /** Structural anchor to highlight in reader view. */
  editHighlightAnchor?: string | null;
  /** Called when user selects text in reader view and wants to comment */
  onSelectionComment?: (quote: string, yPercent: number) => void;
  /** Called when user selects text and clicks "Ask Corner" */
  onAskCornerAboutSelection?: (snippet: { text: string; page?: number; paragraphIndex?: number }) => void;
  /** Pending inline comment form state */
  pendingComment?: { xPercent: number; yPercent: number; pageNumber: number; quote?: string } | null;
  /** Called when inline comment form is submitted */
  onCommentSubmit?: (body: string) => void;
  /** Called when inline comment form is cancelled */
  onCommentCancel?: () => void;
  /** Mobile shell hint: compact document chrome and hide desktop rails. */
  isMobile?: boolean;
  /** Opens the mobile sidebar drawer. */
  onMobileMenu?: () => void;
}

export default function DocumentColumn({
  fileName,
  filePreviewUrl,
  documentId,
  presentingMode = false,
  folders = [],
  currentFolderId,
  onAddToFolder,
  onCreateFolder,
  onToggleFolderContractsKind,
  onDownload,
  onDownloadDocx,
  imageMode = false,
  imageDimensions,
  imageFileSize,
  imageResult,
  onImageActionClick,
  variantLabel,
  onVariantAction,
  emptyStateTitle,
  emptyStateDescription,
  documentVersions = [],
  versionWorkspaceTitle,
  onRenameWorkspace,
  activeLogicalDocumentId,
  activeVersionId,
  versionVerifyResult,
  onSelectDocumentVersion,
  onVerifyDocumentVersion,
  onVersionHistory,
  versionRailPeekKey = 0,
  processing,
  cotState,
  esignFields,
  esignPageCount: _esignPageCount,
  esignSignerName,
  onSignFieldClick,
  esignActiveFieldIndex,
  onEsignNextField,
  linkedTemplateName,
  onSaveFieldToTemplate,
  esignViewerMode,
  esignSigners,
  esignActiveSignerIndex,
  onSelectSigner,
  esignPlacementMode,
  onEsignPlacementModeChange,
  onAddField,
  onDeleteField,
  onConfirmField,
  onMoveField,
  onApplySignature,
  applySignatureDisabled,
  onSendForSignature,
  sendForSignatureDisabled,
  onAddSigningRecipientEmail,
  esignInlinePlacementToolbar = true,
  onLaunchSigning,
  signingButtonState = 'hidden',
  splitPageCount,
  ocrOverlay,
  qrPreview,
  reviewMode,
  reviewSuggestions: _reviewSuggestions,
  reviewActiveSuggestionId: _reviewActiveSuggestionId,
  reviewStats,
  onExitReview,
  onReviewAnnotationClick: _onReviewAnnotationClick,
  reviewDocumentView = 'original',
  onReviewDocumentViewChange,
  reviewUndoEntry,
  onReviewUndo,
  reviewGenerating,
  reviewGeneratingError,
  riskSnapshot,
  onRiskSnapshotClick,
  draftMode = false,
  draftContent,
  draftIsStreaming = false,
  presenceUsers,
  remoteCursors,
  onShare,
  onCursorMove,
  commentCount,
  commentPins,
  activeCommentPinId,
  onCommentPinClick,
  onCommentPinMove,
  onDocumentClick,
  commentMode = false,
  onCommentModeChange,
  readerView = false,
  onReaderViewChange,
  documentText,
  editHighlightAnchor,
  onSelectionComment,
  onAskCornerAboutSelection,
  pendingComment,
  onCommentSubmit,
  onCommentCancel,
  isMobile = false,
  onMobileMenu,
}: DocumentColumnProps) {
  const [zoom, setZoom] = useState(100);
  const [currentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [mobileVersionsOpen, setMobileVersionsOpen] = useState(false);

  const activeVersion = documentVersions.find((version) => version.id === activeVersionId);
  const activeMimeType = activeVersion?.mimeType ?? null;
  const isPdf = isPdfDocument(fileName, activeMimeType);
  const isDocx = isDocxDocument(fileName, activeMimeType);
  const viewerMimeType = isPdf ? 'application/pdf' : activeMimeType;
  const resolvedViewerKind = draftMode
    ? 'draft'
    : filePreviewUrl && isPdf
      ? 'pdf'
      : filePreviewUrl && isDocx
        ? 'docx'
        : filePreviewUrl
          ? 'generic'
          : 'empty';
  // PDFs always get the wide layout — same as presenting mode. Non-PDF
  // recovery copies must not inherit signing/presenting mode into pdf.js.
  const wideLayout = isPdf && (presentingMode || !!filePreviewUrl);
  const pdfViewerOwnsComments = Boolean(filePreviewUrl && isPdf);
  const versionLabel = activeVersion
    ? `v${activeVersion.versionNumber ?? (documentVersions.findIndex((version) => version.id === activeVersion.id) + 1)} · ${isEditableCopyFromSignedPdfVersion(activeVersion) ? 'Editable copy from signed PDF' : activeVersion.fileName ?? activeVersion.changeSummary ?? 'Document'}`
    : documentVersions.length > 0
      ? `${documentVersions.length} versions`
      : null;

  useEffect(() => {
    const contentSource =
      draftMode
        ? 'htmlDraft'
        : filePreviewUrl && isPdf
          ? 'pdfFile'
          : filePreviewUrl && isDocx
            ? 'docxFile'
          : filePreviewUrl
            ? 'mismatch'
            : 'missing';
    docVersionDebugLog('viewer_decision', {
      documentId: activeVersion?.projectId ?? null,
      versionId: activeVersion?.id ?? null,
      contentSource,
      draftMode,
      presentingMode,
      hasPreviewUrl: Boolean(filePreviewUrl),
      fileName: fileName ?? null,
      mimeType: activeVersion?.mimeType ?? null,
      viewer: resolvedViewerKind,
      previewUrl: filePreviewUrl ?? null,
    });
  }, [
    activeVersion?.id,
    activeVersion?.mimeType,
    activeVersion?.projectId,
    draftMode,
    fileName,
    filePreviewUrl,
    isDocx,
    isPdf,
    presentingMode,
    resolvedViewerKind,
  ]);

  console.log('[DocColumn] Render: PAGE_WIDTH=', PAGE_WIDTH, 'PAGE_HEIGHT=', PAGE_HEIGHT, 'zoom=', zoom, 'filePreviewUrl=', !!filePreviewUrl, 'readerView=', readerView, 'presentingMode=', presentingMode, 'wideLayout=', wideLayout, 'esignFields=', esignFields?.length ?? 'undefined');
  useEffect(() => {
    console.log('[DocSurface] visible-file', {
      fileId: documentId ?? activeVersion?.fileId ?? null,
      previewUrl: filePreviewUrl ?? null,
      mimeType: activeMimeType,
      viewer: resolvedViewerKind,
      presentingMode,
      wideLayout,
      esignFields: esignFields?.length,
      activeVersionId,
    });
    console.log('[DocSurface] select-version', {
      versionId: activeVersionId ?? activeVersion?.id ?? null,
      fileId: documentId ?? activeVersion?.fileId ?? null,
      mimeType: activeMimeType,
      viewer: resolvedViewerKind,
    });
  }, [
    activeVersion?.fileId,
    activeVersion?.id,
    activeVersionId,
    activeMimeType,
    documentId,
    esignFields?.length,
    filePreviewUrl,
    presentingMode,
    resolvedViewerKind,
    wideLayout,
  ]);

  return (
    <div
      style={{
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        backgroundColor: APP.bgDocColumn,
        borderRight: `1px solid ${APP.border}`,
      }}
    >
      {/* ── Doc Top Bar ──────────────────────── */}
      {isMobile ? (
        <MobileDocumentHeader
          title={fileName ?? 'Untitled'}
          versionLabel={versionLabel}
          onMenu={onMobileMenu}
          onVersionClick={documentVersions.length > 0 ? () => setMobileVersionsOpen(true) : undefined}
          onShare={onShare}
          onDownload={onDownload}
          onDownloadDocx={onDownloadDocx}
        />
      ) : (
      <div
        style={{
          height: APP.topBarHeight,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          paddingInline: 12,
          gap: 8,
          backgroundColor: '#FAF7F4',
          borderBottom: `1px solid ${APP.borderDocTopBar}`,
        }}
      >
        {/*
          Filename label intentionally omitted. The project banner
          above already shows the project title — repeating the same
          name (often as "title.html") here was pure visual noise. We
          keep this empty flex-grow spacer so the right-side action
          buttons stay anchored to the trailing edge of the bar.
        */}
        <div style={{ flexGrow: 1, flexShrink: 1, flexBasis: '0%' }} aria-hidden="true" />

        {/* Risk snapshot badge */}
        {riskSnapshot && onRiskSnapshotClick && (
          <RiskReviewEntryBanner
            variant="compact"
            nudgeLevel={riskSnapshot.nudgeLevel}
            label={riskSnapshot.label}
            laneLabel={riskSnapshot.laneKey === 'real_estate' ? 'Real Estate' : riskSnapshot.laneKey === 'founder_operator' ? 'Business / Founder' : null}
            jurisdictionLabel={riskSnapshot.jurisdiction}
            ctaLabel="Review"
            onPrimary={onRiskSnapshotClick}
          />
        )}

        {/* Comment mode toggle */}
        {onCommentModeChange && (
          <button
            type="button"
            onClick={() => { console.log('[DocColumn] Comment mode toggle:', !commentMode, 'hasOnDocumentClick=', !!onDocumentClick); onCommentModeChange(!commentMode); }}
            title={commentMode ? 'Exit comment mode (Esc)' : 'Enter comment mode (C)'}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 10px', borderRadius: 6,
              backgroundColor: commentMode ? '#D97645' : '#D976451A',
              border: 'none', cursor: 'pointer', flexShrink: 0,
              transition: 'background-color 0.15s ease',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2.5h10A1.5 1.5 0 0113.5 4v5a1.5 1.5 0 01-1.5 1.5H5L2 13V4A1.5 1.5 0 013.5 2.5H2Z"
                stroke={commentMode ? '#FDFCFB' : '#D97645'} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontFamily: APP.fontFamily, fontSize: 12, fontWeight: 500, color: commentMode ? '#FDFCFB' : '#D97645', lineHeight: '16px' }}>
              {commentCount != null && commentCount > 0 ? commentCount : 'Comment'}
            </span>
          </button>
        )}

        {/* Reader View toggle */}
        {onReaderViewChange && filePreviewUrl && (
          <button
            type="button"
            onClick={() => onReaderViewChange(!readerView)}
            title={readerView ? 'Switch to document view' : 'Switch to reader view (selectable text)'}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 10px', borderRadius: 6,
              backgroundColor: readerView ? '#5E6AD2' : '#5E6AD21A',
              border: 'none', cursor: 'pointer', flexShrink: 0,
              transition: 'background-color 0.15s ease',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 3h10M2 6h10M2 9h7" stroke={readerView ? '#FDFCFB' : '#5E6AD2'} strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <span style={{ fontFamily: APP.fontFamily, fontSize: 12, fontWeight: 500, color: readerView ? '#FDFCFB' : '#5E6AD2', lineHeight: '16px' }}>
              Reader
            </span>
          </button>
        )}

        {/* "Send for signature" — canonical signing entry point.
            See note on onLaunchSigning prop above for the full rationale.
            We render three visual states that are explicit about what
            will happen on click, so the user is not left wondering
            (which was the bug we're fixing). 'in-flight' and 'hidden'
            states render nothing — once mode='esign' takes over, the
            right legend (RecipientLegend) and the SigningGuideCard
            become the active surface for signing actions. */}
        {onLaunchSigning && signingButtonState !== 'hidden' && signingButtonState !== 'in-flight' && (() => {
          const SIGN_GREEN = '#1B7A4B';
          const isAvailable = signingButtonState === 'available';
          const isDraft = signingButtonState === 'draft' || signingButtonState === 'convertible';
          const bg = isAvailable ? SIGN_GREEN : isDraft ? `${SIGN_GREEN}1A` : '#0000000A';
          const fg = isAvailable ? '#FDFCFB' : isDraft ? SIGN_GREEN : APP.textMuted;
          const label = 'Send for signature';
          const tooltip = isAvailable
            ? 'Send this document for signature'
            : isDraft
              ? 'Convert this document to PDF and open signing — click to continue.'
              : 'Convert this file to PDF first to sign it.';
          return (
            <button
              type="button"
              onClick={onLaunchSigning}
              title={tooltip}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 10px', borderRadius: 6,
                backgroundColor: bg,
                border: 'none', cursor: 'pointer', flexShrink: 0,
                transition: 'background-color 0.15s ease',
                fontFamily: APP.fontFamily,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path
                  d="M2.5 11.5h9M3.5 9.5L9 4l1.5 1.5L5 11l-2.5.5.5-2z"
                  stroke={fg}
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span style={{ fontSize: 12, fontWeight: 500, color: fg, lineHeight: '16px' }}>
                {label}
              </span>
            </button>
          );
        })()}

        {/* Presence + Share */}
        {presenceUsers && presenceUsers.length > 0 && (
          <PresenceAvatars users={presenceUsers} />
        )}
        {onShare && (
          <button
            type="button"
            onClick={onShare}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 12px', borderRadius: 6,
              backgroundColor: APP.bgChatSendBtn, border: 'none', cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M4 7.5C4 7.5 5 6 6 6C7 6 8 7.5 8 7.5" stroke="#FDFCFB" strokeWidth="1" strokeLinecap="round" />
              <circle cx="6" cy="3.5" r="1.5" stroke="#FDFCFB" strokeWidth="1" />
              <path d="M9 9C9 9 9.5 8 10 8" stroke="#FDFCFB" strokeWidth="1" strokeLinecap="round" />
              <path d="M3 9C3 9 2.5 8 2 8" stroke="#FDFCFB" strokeWidth="1" strokeLinecap="round" />
            </svg>
            <span style={{ fontFamily: APP.fontFamily, fontSize: 12, fontWeight: 500, color: '#FDFCFB' }}>Share</span>
          </button>
        )}

        {/* Right actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {/* Add to project */}
          <FolderPickerButton
            folders={folders}
            currentFolderId={currentFolderId}
            onSelect={(folderId) => onAddToFolder?.(folderId)}
            onCreateFolder={onCreateFolder}
            onToggleContractsKind={onToggleFolderContractsKind}
          />

        {onVersionHistory && documentVersions.length > 0 && (
          <button
            type="button"
            onClick={onVersionHistory}
            title="Version history"
            aria-label="Version history"
            style={{
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 5,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={APP.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 3-6.7" />
              <path d="M3 4v5h5" />
              <path d="M12 7v5l3 2" />
            </svg>
          </button>
        )}

        {/* Download */}
        {onDownloadDocx && (
          <button
            type="button"
            className="btn-hover"
            onClick={onDownloadDocx}
            title="Download as Word"
            style={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: 5,
              padding: '5px 7px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: APP.fontFamily,
              fontSize: 11,
              fontWeight: 700,
              color: APP.iconDefault,
            }}
          >
            DOCX
          </button>
        )}
        <button
            type="button"
            onClick={onDownload}
            style={{
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 5,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 2v7M4 6.5L7 10l3-3.5M2 11h10" stroke={APP.textSecondary} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
      )}

      {isMobile && (
        <MobileVersionSheet
          open={mobileVersionsOpen}
          onOpenChange={setMobileVersionsOpen}
          versions={documentVersions}
          logicalDocumentId={activeLogicalDocumentId}
          activeVersionId={activeVersionId}
          verifyResult={versionVerifyResult}
          onSelectVersion={onSelectDocumentVersion}
          onVerifyVersion={onVerifyDocumentVersion}
        />
      )}

      {imageMode ? (
        <>
          {/* ── Image Comparison View ──────────── */}
          <div
            style={{
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: '0%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 40,
              gap: 32,
              overflow: 'auto',
              backgroundColor: APP.bgDocViewer,
            }}
          >
            {/* Original */}
            <ImageCard
              src={filePreviewUrl ?? undefined}
              label="Original"
              fileName={`${fileName ?? 'image'}${imageFileSize ? ` · ${imageFileSize}` : ''}`}
              dimensions={imageDimensions}
            />

            {/* Arrow + label */}
            {imageResult && (
              <>
                <TransformArrow label={imageResult.transformLabel} />
                {/* Result */}
                <ImageCard
                  src={imageResult.url}
                  label="Result"
                  fileName={`${imageResult.fileName} · ${imageResult.fileSize}`}
                  badge={imageResult.format}
                  transparent
                />
              </>
            )}
          </div>

          {/* ── Image Action Chips ─────────────── */}
          <ImageActionBar onActionClick={onImageActionClick} />
        </>
      ) : (
        <>
          {/* ── Pagination / Review Mode Bar ──────── */}
          {reviewMode && reviewStats && onExitReview ? (
            <ReviewModeBar stats={reviewStats} onExit={onExitReview} generating={reviewGenerating} generatingError={reviewGeneratingError} />
          ) : (
            <PaginationBar
              currentPage={currentPage}
              totalPages={totalPages}
              zoom={zoom}
              onZoomIn={() => setZoom((z) => Math.min(z + 25, 200))}
              onZoomOut={() => setZoom((z) => Math.max(z - 25, 25))}
              onFitToScreen={() => setZoom(100)}
              variantLabel={variantLabel}
              onVariantAction={onVariantAction}
            />
          )}

          {/* ── Document Viewer ──────────────────── */}
          <div
            style={{
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: '0%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              justifyContent: 'flex-start',
              padding: 0,
              overflow: 'auto',
              backgroundColor: APP.bgDocViewer,
              position: 'relative',
              transition: 'box-shadow 0.2s ease',
            }}
          >
            {!isMobile && (
              <DocumentVersionRail
                versions={documentVersions}
                logicalDocumentId={activeLogicalDocumentId}
                activeVersionId={activeVersionId}
                verifyResult={versionVerifyResult}
                onSelectVersion={onSelectDocumentVersion}
                onVerifyVersion={onVerifyDocumentVersion}
                peekKey={versionRailPeekKey}
                workspaceTitle={versionWorkspaceTitle ?? fileName ?? 'Project'}
                onRenameWorkspace={onRenameWorkspace}
              />
            )}
            <div style={{ flex: 1, minWidth: 0, height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Comment mode banner */}
            {commentMode && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '6px 0',
                  backgroundColor: '#D97645',
                  flexShrink: 0,
                  zIndex: 60,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                  <path d="M2 2.5h10A1.5 1.5 0 0113.5 4v5a1.5 1.5 0 01-1.5 1.5H5L2 13V4A1.5 1.5 0 013.5 2.5H2Z"
                    stroke="#FDFCFB" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ fontFamily: APP.fontFamily, fontSize: 11, fontWeight: 500, color: '#FDFCFB', letterSpacing: '0.02em' }}>
                  Click on the document to add a comment
                </span>
                <button
                  type="button"
                  onClick={() => onCommentModeChange?.(false)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 3,
                    marginLeft: 4, padding: '2px 8px', borderRadius: 4,
                    backgroundColor: 'rgba(255,255,255,0.2)', border: 'none',
                    cursor: 'pointer', color: '#FDFCFB', fontFamily: APP.fontFamily,
                    fontSize: 10, fontWeight: 500,
                  }}
                >
                  Esc to exit
                </button>
              </div>
            )}
            <div
              style={{
                flexGrow: 1,
                width: '100%',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                padding: wideLayout ? '10px 4px' : 20,
                position: 'relative',
              }}
            >
            {/* Block whole viewer only when not streaming a draft — drafting shows live text in the editor. */}
            {processing && !draftMode && <ProcessingOverlay />}
            {processing && draftMode && <DraftStreamingIndicator label={processing.label} progress={processing.progress} />}
            {remoteCursors && remoteCursors.size > 0 && (
              <LiveCursors cursors={remoteCursors} currentPage={currentPage} />
            )}
            {/* Zoom wrapper — scales the page container */}
            <div style={{
              transformOrigin: 'top center',
              transform: zoom !== 100 ? `scale(${zoom / 100})` : undefined,
              transition: 'transform 0.15s ease',
              ...(wideLayout ? { width: '100%' } : {}),
            }}>
            {/* Document page container — owns comment pins, cursor tracking, and comment-mode clicks */}
            <div
              style={{
                position: 'relative',
                width: wideLayout ? '100%' : PAGE_WIDTH,
                maxWidth: '100%',
                cursor: commentMode ? 'crosshair' : undefined,
                ...(commentMode ? { boxShadow: 'inset 0 0 0 2px #D9764580', borderRadius: 4 } : {}),
                transition: 'box-shadow 0.15s ease',
              }}
              onMouseMove={onCursorMove ? (e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * 100;
                const y = ((e.clientY - rect.top) / rect.height) * 100;
                onCursorMove(x, y, currentPage);
              } : undefined}
              onClick={(e) => {
                console.log('[DocColumn] Page container clicked. commentMode=', commentMode, 'hasOnDocumentClick=', !!onDocumentClick, 'pendingComment=', !!pendingComment, 'target=', (e.target as HTMLElement).tagName);
                if (pdfViewerOwnsComments) return;
                if (!commentMode || !onDocumentClick) return;
                // If a comment form is already open, dismiss it instead of opening a new one
                if (pendingComment && onCommentCancel) {
                  console.log('[DocColumn] Dismissing existing comment form');
                  onCommentCancel();
                  return;
                }
                const rect = e.currentTarget.getBoundingClientRect();
                const xPercent = (e.clientX - rect.left) / rect.width;
                const yPercent = (e.clientY - rect.top) / rect.height;
                console.log('[DocColumn] Placing comment at', { xPercent: xPercent.toFixed(3), yPercent: yPercent.toFixed(3), page: currentPage });
                onDocumentClick(xPercent, yPercent, currentPage);
              }}
            >
              {/* Comment pins — positioned relative to this page container */}
              {!pdfViewerOwnsComments && commentPins && commentPins.length > 0 && (
                <CommentPins pins={commentPins} activePinId={activeCommentPinId} onPinClick={onCommentPinClick} onPinMove={onCommentPinMove} currentPage={currentPage} />
              )}

              {/* Inline comment form — shown when a comment anchor is pending */}
              {!pdfViewerOwnsComments && pendingComment && onCommentSubmit && onCommentCancel && (
                <div style={{
                  position: 'absolute',
                  left: `${Math.min(pendingComment.xPercent * 100, 60)}%`,
                  top: `${pendingComment.yPercent * 100}%`,
                  zIndex: 55,
                  transform: 'translate(0, 4px)',
                }}>
                  <InlineCommentForm
                    quote={pendingComment.quote}
                    onSubmit={onCommentSubmit}
                    onCancel={onCommentCancel}
                  />
                </div>
              )}

              {readerView && documentText ? (
                <DocumentTextView
                  paragraphs={documentText.paragraphs}
                  loading={documentText.loading}
                  error={documentText.error}
                  onSelectionComment={onSelectionComment}
                  onAskCornerAboutSelection={onAskCornerAboutSelection}
                  commentMode={commentMode}
                  onPageCountChange={setTotalPages}
                  highlightAnchor={editHighlightAnchor}
                  blockAnchors={documentText.blockAnchors}
                />
              ) : qrPreview ? (
                <div
                  style={{
                    width: '100%',
                    minHeight: PAGE_HEIGHT,
                    borderRadius: 4,
                    padding: '40px 48px',
                    backgroundColor: APP.bgDocSheet,
                    boxShadow: APP.docSheetShadow,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 16,
                  }}
                >
                  <div
                    style={{
                      width: 280,
                      height: 280,
                      borderRadius: 12,
                      border: '1px solid #E8E0D8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={qrPreview.imageUrl}
                      alt="QR code"
                      style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 20 }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 500,
                        color: '#2B2420',
                        fontFamily: APP.fontFamily,
                        lineHeight: '20px',
                      }}
                    >
                      {qrPreview.payload}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: APP.textSecondary,
                        fontFamily: APP.fontFamily,
                        lineHeight: '16px',
                      }}
                    >
                      Scan to open · {qrPreview.format.toUpperCase()} · {qrPreview.sizePx}×{qrPreview.sizePx}px
                    </span>
                  </div>
                </div>
              ) : draftMode ? (
                processing && !(draftContent ?? '').trim() ? (
                  <DraftSkeletonDocument />
                ) : (
                  <PaginatedDocument
                    html={draftContent ?? ''}
                    streaming={draftIsStreaming}
                    pageSize="letter"
                    onAskCornerAboutSelection={onAskCornerAboutSelection}
                  />
                )
              ) : splitPageCount && splitPageCount > 0 ? (
                <div
                  style={{
                    width: '100%',
                    minHeight: PAGE_HEIGHT,
                    borderRadius: 4,
                    padding: '40px 48px',
                    backgroundColor: APP.bgDocSheet,
                    boxShadow: APP.docSheetShadow,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14,
                  }}
                >
                  <SplitPageStrip pageCount={splitPageCount} />
                </div>
              ) : filePreviewUrl && isPdf ? (
                // CORNER_V2: Corner-owned viewer for ALL PDF files — signing, presenting, and plain preview.
                // Prevents Chrome's built-in PDF viewer from ever flashing.
                <div style={{
                  width: '100%',
                  height: isMobile ? 'calc(100dvh - 132px)' : 'calc(100vh - 132px)',
                  minHeight: 0,
                  borderRadius: isMobile ? 0 : 4,
                  overflow: 'hidden',
                  boxShadow: isMobile ? 'none' : APP.docSheetShadow,
                }}>
                  <PdfSigningViewer
                    pdfUrl={filePreviewUrl}
                    mimeType={viewerMimeType ?? undefined}
                    documentId={documentId}
                    fields={esignFields ?? []}
                    activeFieldId={(esignFields && esignActiveFieldIndex != null && esignActiveFieldIndex >= 0)
                      ? (esignFields[esignActiveFieldIndex]?.id ?? null)
                      : null}
                    onFieldClick={(fieldId) => {
                      const field = esignFields?.find((f) => f.id === fieldId);
                      if (field && onSignFieldClick) onSignFieldClick(field);
                    }}
                    onNextField={onEsignNextField}
                    mode={esignViewerMode ?? 'sign'}
                    signerName={esignSignerName}
                    showThumbnails={!isMobile}
                    isMobile={isMobile}
                    linkedTemplateName={linkedTemplateName}
                    onSaveFieldToTemplate={onSaveFieldToTemplate}
                    // v1 placement-redesign: forwarded only when the parent
                    // has opted in by providing a signers list. Without
                    // these the viewer falls back to legacy sign-mode UX.
                    signers={esignSigners}
                    activeSignerIndex={esignActiveSignerIndex}
                    onSelectSigner={onSelectSigner}
                    placementMode={esignPlacementMode}
                    onPlacementModeChange={onEsignPlacementModeChange}
                    onAddField={onAddField}
                    onDeleteField={onDeleteField}
                    onConfirmField={onConfirmField}
                    onMoveField={onMoveField}
                    onApplySignature={onApplySignature}
                    applySignatureDisabled={applySignatureDisabled}
                    onSendForSignature={onSendForSignature}
                    sendForSignatureDisabled={sendForSignatureDisabled}
                    onAddRecipientEmail={onAddSigningRecipientEmail}
                    inlinePlacementToolbar={esignInlinePlacementToolbar}
                    onAskCornerAboutSelection={onAskCornerAboutSelection
                      ? (s) => onAskCornerAboutSelection({ text: s.text, page: s.page })
                      : undefined}
                    commentPins={commentPins}
                    activeCommentPinId={activeCommentPinId}
                    onCommentPinClick={onCommentPinClick}
                    onCommentPinMove={onCommentPinMove}
                    commentMode={commentMode}
                    pendingComment={pendingComment}
                    onDocumentCommentClick={(xPercent, yPercent, pageNumber) => {
                      if (!commentMode || !onDocumentClick) return;
                      if (pendingComment && onCommentCancel) {
                        onCommentCancel();
                        return;
                      }
                      onDocumentClick(xPercent, yPercent, pageNumber);
                    }}
                    onCommentSubmit={onCommentSubmit}
                    onCommentCancel={onCommentCancel}
                  />
                </div>
              ) : filePreviewUrl && isDocx ? (
                <EditableDocxRecoverySurface
                  fileName={fileName}
                  filePreviewUrl={filePreviewUrl}
                  activeVersion={activeVersion}
                  onDownload={onDownload}
                />
              ) : filePreviewUrl ? (
                // Browser-native iframe for non-PDF files (DOCX, images, etc).
                <div style={{ position: 'relative', width: '100%' }}>
                  <iframe
                    src={filePreviewUrl}
                    onLoad={(e) => { console.log('[DocColumn] iframe loaded, src=', (e.target as HTMLIFrameElement).src?.slice(0, 100)); }}
                    onError={(e) => { console.error('[DocColumn] iframe error:', e); }}
                    title="Document preview"
                    style={{
                      width: '100%',
                      minHeight: PAGE_HEIGHT,
                      borderRadius: 4,
                      backgroundColor: APP.bgDocSheet,
                      boxShadow: APP.docSheetShadow,
                      border: 'none',
                      display: 'block',
                    }}
                  />
                  {/* Comment mode: overlay on top of iframe to intercept clicks (iframe swallows events) */}
                  {commentMode && onDocumentClick && (
                    <div style={{ position: 'absolute', inset: 0, zIndex: 10, cursor: 'crosshair', borderRadius: 4 }} />
                  )}
                  {ocrOverlay && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: 12,
                        padding: '6px 14px',
                        borderRadius: 6,
                        backgroundColor: 'color-mix(in srgb, var(--app-bg-version-card) 92%, transparent)',
                        backdropFilter: 'blur(8px)',
                        fontFamily: APP.fontFamily,
                        fontSize: 12,
                        lineHeight: '16px',
                      }}
                    >
                      <span style={{ color: '#D46A3A', fontWeight: 500 }}>
                        {ocrOverlay.confidence}% confidence
                      </span>
                      <span style={{ color: APP.textSecondary, fontWeight: 400 }}>
                        {ocrOverlay.wordCount} words extracted
                      </span>
                    </div>
                  )}
                </div>
              ) : processing ? (
                <LiveDocumentDraftingPanel label={processing.label} cotState={cotState} />
              ) : (
                <div
                  style={{
                    width: '100%',
                    minHeight: PAGE_HEIGHT,
                    borderRadius: 4,
                    padding: '40px 48px',
                    backgroundColor: APP.bgDocSheet,
                    boxShadow: APP.docSheetShadow,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: APP.textMuted,
                    fontFamily: APP.fontFamily,
                    fontSize: 13,
                    flexDirection: 'column',
                    gap: 8,
                    textAlign: 'center',
                    lineHeight: '18px',
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 700, color: APP.textPrimary }}>
                    {emptyStateTitle ?? 'No document loaded'}
                  </div>
                  {emptyStateDescription && (
                    <div style={{ maxWidth: 340, color: APP.textMuted, fontSize: 12, lineHeight: '17px' }}>
                      {emptyStateDescription}
                    </div>
                  )}
                </div>
              )}

              {/* CORNER_V2: REMOVED — legacy SignatureFieldOverlay sidebar card list.
                 Field overlays are now built into PdfSigningViewer above.
                 The old overlay rendered field cards in a list below the iframe.
                 PdfSigningViewer renders them directly on the PDF canvas. */}
            </div>
            </div>{/* end zoom wrapper */}

            {/* Review mode: undo toast */}
            {reviewMode && reviewUndoEntry && onReviewUndo && (
              <ReviewUndoToast
                sectionRef={reviewUndoEntry.sectionRef}
                action={reviewUndoEntry.action}
                onUndo={onReviewUndo}
              />
            )}
            </div>{/* end inner wrapper */}
            </div>
          </div>

          {/* ── Review Mode: Doc Controls + Toggle ── */}
          {reviewMode && onReviewDocumentViewChange && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: 32,
                flexShrink: 0,
                paddingInline: 16,
                borderTop: `1px solid ${APP.border}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: APP.textSecondary, fontFamily: APP.fontFamily, lineHeight: '14px' }}>
                  {currentPage} / {totalPages}
                </span>
                <span style={{ fontSize: 11, color: APP.textSecondary, fontFamily: APP.fontFamily }}>|</span>
                <span style={{ fontSize: 11, color: APP.textSecondary, fontFamily: APP.fontFamily, lineHeight: '14px' }}>
                  {zoom}%
                </span>
              </div>
              <DocViewToggle view={reviewDocumentView} onChange={onReviewDocumentViewChange} />
            </div>
          )}

        </>
      )}
    </div>
  );
}
